import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, debounceTime, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import { BreadcrumbService, MasterData, ModalService, SiteService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { reportInvalidFormFields } from '@app/utilities';
import { WithholdingDocumentProxyService } from '../../../proxies/withholding-document.proxy.service';
import { environment } from '../../../../environments/environment';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { exchangeRateBoundsFor, isPlausibleExchangeRate } from '../../../utilities/exchange-rate-bounds';
import { ApNoteLinePreview, previewCreditSplit, previewNoteLine } from '../../../utilities/ap-note-preview.util';
import { ApDocumentQuickViewModalComponent } from '../../shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import {
  AMOUNT_ONLY_AP_DOCUMENT_TYPES,
  AP_DOCUMENT_ENTITY_CODE,
  ApDocument,
  ApDocumentInsert,
  ApDocumentLine,
  ApDocumentLineInsert,
  ApDocumentStatus,
  ApDocumentType,
  ApNoteAdjustment,
  ApNoteCause,
  ApNotePriceMode,
  ApNoteReason,
  ApRateSource,
  NOTE_AP_DOCUMENT_TYPES,
  PayableDocument,
  PaymentTermSource,
  PurchaseReceiptNextNumber,
  S3Object,
  Currency,
  ORDER_INVOICE_ENTITY_CODE,
  ORDER_RECEIPT_ENTITY_CODE,
} from '../../../models/ap-document/ap-document';
import { InvoiceOrderProxyService, ORDER_DOCUMENT_STATUS_APPROVED, PreInvoiceDto } from '../../../proxies/invoice-order.proxy.service';
import { PaymentTermsProxyService } from '../../../proxies/payment-terms.proxy.service';
import { ResponseTranslation } from '../../../models/Translations';
import { PreReceiptDto, ReceiptOrderProxyService, ReceiptType } from '../../../proxies/receipt-order.proxy.service';
import { ApDocumentFormGroup, ApDocumentLineFormGroup } from '../../../models/ap-document/ap-document-form';
import { ApDocumentProxyService, PoItemInvoiced, WithholdingClassificationRules } from '../../../proxies/ap-document.proxy.service';
import { AccountingPeriodProxyService } from '../../../proxies/accounting-period.proxy.service';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { ExchangeRateProxyService } from '../../../proxies/exchange-rate.proxy.service';
import { FinancialAccountProxyService } from '../../../proxies/financial-account.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { ItemMasterProxyService, ItemMasterDto } from '../../../proxies/item-master.proxy.service';
import { MasterDataProxyService } from '../../../proxies/master-data.proxy.service';
import {
  PurchaseOrderProxyService,
  PurchaseOrderDto,
  ItemInPurchaseOrderDto,
  PURCHASE_ORDER_STATUS_CANCELLED,
  PURCHASE_ORDER_TYPE_ADMINISTRATIVE,
  PURCHASE_ORDER_TYPE_STANDARD,
} from '../../../proxies/purchase-order.proxy.service';
import { TaxAuthorizationProxyService } from '../../../proxies/tax-authorization.proxy.service';
import {
  AUTHORIZATION_COUNTRY_OPTIONS,
  AP_DOCUMENT_ENTITY_DEFINITION_CODE,
  isSelfIssuedApDocument,
} from '../../../models/tax-authorization/tax-authorization';
import { Business, BusinessEntityType } from '../../../models/business';
import { AdministrativePurchaseOrderRoles, SupportRoles } from '../../../models/roles/roles-group';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import _ from 'lodash';

interface EnumOption {
  label: string;
  value: number;
}

/** #4767 — which Order document a Pre-Invoice is registered from. */
type PreInvoiceSource = 'invoice' | 'receipt';

/**
 * #4767 — one row in the Pre-Invoice picker, normalized so the Order Invoice and the Order Receipt share a
 * single autocomplete/selection path. Exactly one of `invoice` / `receipt` is set; that is what decides the
 * PreInvoiceReference entity code and how the AP lines are built.
 */
interface PreInvoiceOption {
  id: string;
  /** Our number for the source document — invoice number or receipt number. */
  number: string;
  providerInvoiceNumber: string | null;
  /** Provider's fiscal (CAI) document number — receipts only. */
  fiscalInvoiceNumber: string | null;
  totalAmount: number;
  /** PO numbers behind the source — invoices only (a receipt has no PO). */
  purchaseOrderNumbers: string[];
  invoice?: PreInvoiceDto;
  receipt?: PreReceiptDto;
}

/** Lines grouped by the Purchase Order they belong to (for the grouped line-items view). */
interface PoGroup {
  reference: string;
  number: string;
  lines: { index: number; group: FormGroup<ApDocumentLineFormGroup> }[];
}

/** #4842 — the master-data category the ItemClassification catalog lives under (see ItemMaster's
 * [MasterDataCategory("ItemClassification")] attribute in Item.API). */
const ITEM_CLASSIFICATION_CATEGORY = 'ItemClassification';

/** Debounce before the PO picker re-queries Order.API by number, so POs beyond the first page are found —
 * not just the preloaded set. The instant local filter keeps the loaded list responsive meanwhile. */
const PO_SEARCH_DEBOUNCE_MS = 700;

/** Page size for the PO picker (#4785). Small enough that the section stays scannable when expanded;
 *  the paginator reaches the rest rather than the old hard cap of 25 hiding them. */
const PO_PAGE_SIZE = 10;

@Component({
  selector: 'app-ap-document-details',
  templateUrl: './ap-document-details.component.html',
  styleUrl: './ap-document-details.component.scss',
})
export class ApDocumentDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(ApDocumentProxyService);
  private readonly businessService = inject(BusinessProxyService);
  private readonly exchangeRateService = inject(ExchangeRateProxyService);
  private readonly itemService = inject(ItemMasterProxyService);
  private readonly masterDataService = inject(MasterDataProxyService);
  private readonly purchaseOrderService = inject(PurchaseOrderProxyService);
  /** #5166 — used only to ask whether a certificate already cites this document. */
  private readonly withholdingDocumentService = inject(WithholdingDocumentProxyService);
  private readonly invoiceOrderService = inject(InvoiceOrderProxyService);
  private readonly receiptOrderService = inject(ReceiptOrderProxyService);
  private readonly caiService = inject(TaxAuthorizationProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly auth = inject(UserAuthenticationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly accountingPeriodService = inject(AccountingPeriodProxyService);
  private readonly paymentTermsService = inject(PaymentTermsProxyService);
  private readonly financialAccountService = inject(FinancialAccountProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly modalService = inject(ModalService);

  /** #4784 — API-owned payment-term → credit-days map, loaded once so the preview can resolve a term. */
  private readonly creditDaysByTerm = signal<Map<string, number>>(new Map());

  /** #4714 — advisory (non-blocking) posting-date deviation warning shown near the posting date field. */
  readonly postingDateWarning = signal<string | null>(null);

  /**
   * #4783 — advisory (non-blocking) invoice-age warning shown under the document date field. Separate from
   * the posting-date deviation above: that one guards the GL period (15 days, either direction), this one
   * guards a stale vendor invoice (60 days, past only).
   */
  readonly invoiceAgeWarning = signal<string | null>(null);

  // ── #4784 · payment term (API-resolved, read-only here) ────────────────────────────────────────────
  /** The term the server resolved for this document; null until it has been through a save/load. */
  readonly paymentTermCode = signal<string | null>(null);
  readonly paymentTermSource = signal<PaymentTermSource>(PaymentTermSource.ProviderDefault);
  readonly providerPaymentTermCode = signal<string | null>(null);
  /** Credit days as resolved by the server. Null pre-save, when only the provider default is knowable. */
  readonly resolvedCreditDays = signal<number | null>(null);

  /** True when the term came from the originating PO / Receipt rather than the provider master. */
  readonly paymentTermFromSourceDocument = computed(() => this.paymentTermSource() !== PaymentTermSource.ProviderDefault);

  /** True when the source document's term differs from the provider's standing default — worth flagging. */
  readonly paymentTermDiffersFromProvider = computed(() => {
    const resolved = this.paymentTermCode();
    const providerDefault = this.providerPaymentTermCode();
    return !!resolved && !!providerDefault && resolved !== providerDefault;
  });

  /**
   * #4785 — the term may be overridden ONLY when no source document dictates it. A PO- or Receipt-sourced
   * term is authoritative: correcting it means correcting that document, so the field stays locked. Mirrors
   * the server rule in ResolvePaymentTermAsync, which discards a client term in that case regardless.
   * #6324 — a debit note may override the term it copied from its original; a credit note has no term.
   */
  readonly canOverridePaymentTerm = computed(
    () =>
      this.isDraft() && !this.isCreditNote() && (!this.paymentTermFromSourceDocument() || (this.isDebitNote() && !this.isFinancialNote())),
  );

  /** #4785 — set when the user clicks Override; unlocks the term dropdown for this document. */
  readonly paymentTermOverrideOn = signal(false);

  /** #4785 — read-only functional amount for the rail. Uses the API's value once saved, else previews it.
   *
   *  The rate here is a rate TO USD — units of the document currency per 1 USD (ExchangeRate.rateToUSD, and
   *  the Order module's exchangeRateToUSD), so the functional amount is total ÷ rate, NOT total × rate:
   *  HNL 2,475 at 24.75 per USD is USD 100. The API multiplies instead, but by a different number — it
   *  resolves a true from→functional multiplier via CurrencyConverter.CrossRate (= toUsd / fromUsd), which
   *  for a USD functional currency is 1 / rateToUSD. Same conversion, reciprocal inputs. */
  get functionalAmountDisplay(): number | null {
    // A POSTED document's functional amount is booked history: it is what the entry actually carries, and
    // nothing on this screen can change it. Show it verbatim.
    //
    // A DRAFT is the opposite, and this used to get it backwards. A draft is re-priced from scratch on every
    // save — UpdateDraftAsync → BuildDraftAsync → ResolveExchangeRateAsync — so the stored figure is only
    // the LAST save's answer. The moment the rate on this panel differs from the one that produced it (a
    // typed override, a reload that retired one, a changed document date), showing the stored amount states
    // a conversion the document is about to stop having. Preview from the rate that will actually apply.
    if (!this.isDraft()) {
      const saved = this.form.controls.functionalAmount.value;
      if (saved !== null && saved !== undefined) return saved;
    }
    const rate = this.effectiveRate();
    return rate !== null && rate > 0 ? Math.round((this.subtotal() / rate) * 100) / 100 : null;
  }

  // ── AP control account (pinned at registration) ────────────────────────────────────────────────────
  // A saved document credits the account it was REGISTERED with, for good: the API pins it and ignores both
  // the client's value and whatever the provider master says today. So the readout has to come off the
  // document, not off the provider — otherwise re-pointing (or clearing) the provider's account would make
  // the form show an account this document does not and will not post to.

  /** The account the saved document is pinned to. All null on a new document — nothing is pinned yet, so
   *  the readout falls back to the provider's. */
  readonly documentApAccountReference = signal<string | null>(null);
  readonly documentApAccountNumber = signal<string | null>(null);
  readonly documentApAccountName = signal<ResponseTranslation | null>(null);

  /** The provider master's account as it stands TODAY — API-hydrated, display-only, and only ever compared
   *  against the pinned one. Null once the provider's account has been cleared. */
  readonly providerCurrentApAccountReference = signal<string | null>(null);
  readonly providerCurrentApAccountNumber = signal<string | null>(null);

  /** True when the pinned account is no longer the provider's current one — either the provider was
   *  re-pointed to a different account or had its own cleared. Purely informational: the document keeps
   *  posting to its own account either way, which is what the notice next to the readout says. */
  readonly apAccountDiffersFromProvider = computed(() => {
    const pinned = this.documentApAccountReference();
    if (!pinned) return false;
    return pinned !== this.providerCurrentApAccountReference();
  });

  /** Spells out both sides of the divergence, since "differs" alone leaves the clerk guessing which is which. */
  get apAccountDivergenceTooltip(): string {
    const current = this.providerCurrentApAccountNumber();
    const provider = current
      ? `${this.t('ApDocument_ApAccount_ProviderNow', 'The provider now uses')} ${current}.`
      : this.t('ApDocument_ApAccount_ProviderNone', 'The provider has no AP account set right now.');
    return `${provider} ${this.t(
      'ApDocument_ApAccount_Differs',
      'This document keeps the account it was registered with and will continue to post to it.',
    )}`;
  }

  /** #4785 — AP control account number: the document's own once it has one, else the provider's (new doc). */
  get effectiveApAccountNumber(): string {
    return this.documentApAccountNumber() ?? this.selectedProvider()?.providerDetails?.apGlAccountNumber ?? '';
  }

  /** #4785 — AP control account name (localized; render through the `localized` pipe), with the number
   *  shown beneath it. Once the document has its own account the name must describe THAT account — never
   *  fall through to the provider's, which by then may be naming a different one. */
  get effectiveApAccountName(): ResponseTranslation | null {
    if (this.documentApAccountReference()) return this.documentApAccountName();
    return this.selectedProvider()?.providerDetails?.apGlAccountName ?? null;
  }

  /**
   * Looks the pinned account up in the chart of accounts for its name. Only needed when the document has no
   * name snapshot of its own: documents registered before #4785 never had one, and documents saved while
   * their provider's account was cleared had theirs wiped by the old unconditional re-snapshot. Without this
   * the readout falls back to a bare account number.
   */
  private resolveDocumentApAccountName(reference: string): void {
    this.financialAccountService.getById(reference).subscribe({
      next: (response) => {
        const account = response.data;
        if (account?.accountName) this.documentApAccountName.set(account.accountName);
        if (!this.documentApAccountNumber() && account?.accountNumber) this.documentApAccountNumber.set(account.accountNumber);
      },
      error: () => undefined,
    });
  }

  /** #4785 — "2026/06/14 + 45 days = 2026/07/29", so the derivation is visible rather than implied. */
  get dueDateDerivation(): string | null {
    const doc = this.form.controls.documentDate.value;
    const due = this.form.controls.dueDate.value;
    if (!doc || !due) return null;
    return `${this.formatDate(doc)} + ${this.effectiveCreditDays} ${this.t('ApDocument_Days', 'days')} = ${this.formatDate(due)}`;
  }

  /**
   * Formats a date the USER picked — a `p-datepicker` value, which is local midnight of the chosen day.
   * Local getters are correct here: the picker built the Date in the browser's zone.
   */
  private formatDate(value: Date): string {
    const y = value.getFullYear();
    const m = `${value.getMonth() + 1}`.padStart(2, '0');
    const d = `${value.getDate()}`.padStart(2, '0');
    return `${y}/${m}/${d}`;
  }

  /**
   * Formats a pure DATE the API supplied (#5234) — a received date, stamped at Honduras local midnight.
   *
   * UTC getters, deliberately: these carry no time of day worth honouring, and reading them with local
   * getters is the display half of the day-early bug. A receipt completed 2026-09-03T21:53Z arrives from
   * the API as 2026-09-03T06:00Z; `getDate()` on that returns 2 anywhere west of UTC-6, `getUTCDate()`
   * returns 3 everywhere.
   *
   * Day-first `dd/MM/yyyy`, which is how dates read everywhere else in this app and how they read in
   * Honduras — not the `yyyy/MM/dd` of `formatDate`, whose one caller spells out an arithmetic
   * derivation where the ordered form is easier to follow.
   */
  private formatApiDate(value: Date): string {
    const y = value.getUTCFullYear();
    const m = `${value.getUTCMonth() + 1}`.padStart(2, '0');
    const d = `${value.getUTCDate()}`.padStart(2, '0');
    return `${d}/${m}/${y}`;
  }

  /** #4785 — unlock the term for editing. Only reachable when canOverridePaymentTerm() is true. */
  onOverridePaymentTerm(): void {
    if (!this.canOverridePaymentTerm()) return;
    this.paymentTermOverrideOn.set(true);
    this.form.controls.paymentTermCode.enable({ emitEvent: false });
  }

  onOverrideRate(): void {
    if (!this.canOverrideRate()) return;
    this.rateOverrideOn.set(true);
  }

  /** Abandons the override and hands the document back to its ordinary sources. Clearing the control is what
   *  makes that real rather than cosmetic: a blank rateToUsd is precisely what tells the API to fall through
   *  to the source document, the PO, then the day. */
  onCancelRateOverride(): void {
    this.rateOverrideOn.set(false);
    this.form.controls.rateToUsd.setValue(null);
    // A manual booking is only in force while the override still carries it. Dropping the override sends a
    // null rateToUsd, which makes the next save re-resolve from the source document, the PO or the table —
    // so leaving the old manual figure leading the panel would report a rate the document is about to lose.
    // Only the Manual source is retired: a rate booked FROM a source document or a PO is re-resolved to the
    // same number on save, so it is still what applies.
    if (this.bookedRateSource() === ApRateSource.Manual) {
      this.bookedRateToUsd.set(null);
      this.bookedRateSource.set(null);
    }
  }

  /**
   * Refetch the rate for the document's date, and drop any override first.
   *
   * <p>For the case the panel could not handle at all before: the table has no rate for that day, finance
   * publishes one while the document is open, and the only recourse was reloading the page and retyping
   * everything.</p>
   *
   * <p><b>The override has to go for this to mean anything.</b> A typed rate outranks every other source —
   * here and in the API — so refetching underneath one changes nothing anybody can see: the panel goes on
   * reporting "Rate you entered" and the figure you were trying to replace, which reads as the reload
   * having silently failed. Asking for the rate again IS abandoning the one you typed.</p>
   */
  reloadRate(): void {
    this.onCancelRateOverride();
    this.loadRateInForce(this.form.controls.currency.value);
  }

  readonly ApDocumentStatus = ApDocumentStatus;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  status = signal<number>(ApDocumentStatus.Draft);
  headerTitle = signal<string>(this.t('ApDocument_New', 'New AP Document'));
  documentNumber = signal<string>('');

  // Picker suggestion state
  /** #4842 — ItemClassification catalog, for the picker on description-only lines. */
  itemClassifications = signal<MasterData[]>([]);

  // Pre-Invoice mode (#4306): register an AP document from an Order Invoice. The server resolves the
  // provider + lines from the invoice's POs on create, so the provider/PO pickers are hidden in this mode.
  usePreInvoice = signal(false);
  selectedPreInvoice = signal<PreInvoiceOption | null>(null);
  /** Source invoice number, shown as a badge on a document that was registered from a pre-invoice. */
  preInvoiceNumber = signal<string | null>(null);
  /** #4767 — a LOADED document whose pre-invoice source was an Order Receipt, so the badge names it
   * correctly. Read from the saved reference's entity code (the picker state is gone by then). */
  loadedFromReceipt = signal(false);
  /** #4767 — which Order document the pre-invoice is drawn from. The AP document type is unchanged by
   * this: it only switches which collection the picker searches and how the lines are built. */
  /** Which Order collection the Pre-Invoice picker searches. Pinned to 'invoice': the source toggle is no
   *  longer rendered, so nothing moves this off its initial value on a NEW document. A saved document's own
   *  source is read from its PreInvoiceReference's entity code, not from here. */
  preInvoiceSource = signal<PreInvoiceSource>('invoice');
  /** The two sources, kept alongside {@link setPreInvoiceSource} as the way back: re-rendering the
   *  p-selectButton in the template is all that re-enables the receipt source (#4767). Not offered today. */
  readonly preInvoiceSourceOptions: { label: string; value: PreInvoiceSource }[] = [
    { label: this.t('ApDocument_PreInvoice_Source_Invoice', 'Pre-Invoice'), value: 'invoice' },
    { label: this.t('ApDocument_PreInvoice_Source_Receipt', 'Receipt'), value: 'receipt' },
  ];
  /** The provider's CAIs (from the TaxAuthorization collection) for the CAI dropdown. */
  caiOptions = signal<{ label: string; value: string }[]>([]);

  // Purchase orders: the provider's open POs feed the line-items picker; lines are grouped per PO.
  availablePos = signal<PurchaseOrderDto[]>([]);
  addedPoIds = signal<Set<string>>(new Set());
  poGroups = signal<PoGroup[]>([]);
  /** #4725 — general charge/fee lines (no PO reference) shown in their own section on PO-driven Invoices. */
  chargeLines = signal<{ index: number; group: FormGroup<ApDocumentLineFormGroup> }[]>([]);
  /** #4724 — per-PO-item invoiced-vs-ordered info, keyed by PurchaseOrderItemReference. Populated when a PO
   * is added (or a draft with PO lines loads); drives the already-invoiced note + the over-invoice block. */
  poInvoicedByItem = signal<Map<string, PoItemInvoiced>>(new Map());

  readonly poSearchControl = new FormControl<string>('', { nonNullable: true });
  private readonly poSearch = toSignal(this.poSearchControl.valueChanges, { initialValue: '' });
  /** Reactive copy of the document currency — the PO picker shows only POs of this currency.
   *
   * Seeded to the same default as `form.controls.currency`, and it has to stay that way: nothing emits
   * `valueChanges` on a fresh form, so if the two disagreed a new document would filter the PO list by
   * one currency while its header claimed the other, until the clerk happened to touch the dropdown. */
  readonly selectedCurrency = signal<Currency>(Currency.USD);
  readonly filteredPos = computed(() => {
    const q = (this.poSearch() ?? '').toLowerCase().trim();
    const currency = this.selectedCurrency();
    // Only POs in the document's currency are addable (one document = one currency).
    let pos = this.availablePos().filter((po) => po.currency === currency);
    if (q) pos = pos.filter((po) => (po.purchaseOrderNumber ?? '').toLowerCase().includes(q));
    return pos;
  });

  // ── PO picker paging (#4785) ───────────────────────────────────────────────────────────────────────
  /** 1-based page of the provider's open POs currently loaded. */
  readonly poPage = signal(1);
  readonly poPageSize = PO_PAGE_SIZE;
  /** Total matching POs server-side — drives the paginator, so more than one page is reachable. */
  readonly poTotalRecords = signal(0);
  /** 0-based first row, for p-paginator. */
  readonly poFirst = computed(() => (this.poPage() - 1) * this.poPageSize);

  /**
   * Full details of every PO already added to the document, kept so an added PO stays visible even when the
   * current search or page doesn't include it — paging away must never hide what is already on the document.
   * Seeded from the line groups on a loaded draft (number only) and enriched when a PO is added.
   */
  readonly addedPoDetails = signal<Map<string, PurchaseOrderDto>>(new Map());

  /** Added POs pinned to the top, then this page's results with the pinned ones removed. */
  readonly visiblePos = computed<PurchaseOrderDto[]>(() => {
    const results = this.filteredPos();
    const resultIds = new Set(results.map((po) => po.id));
    const pinned = Array.from(this.addedPoDetails().values()).filter((po) => po.id && !resultIds.has(po.id));
    return [...pinned, ...results];
  });

  /** Top-of-panel item search — kept for non-PO document types (free item search). */
  readonly lineItemSearch = new FormControl<ItemMasterDto | null>(null);

  selectedProvider = signal<Business | null>(null);

  /** The rate the table gives for this document's date — NOT today's. Named for what it is: the API books
   *  every rate against DocumentDate, so "the daily rate" was only ever right for a document dated today. */
  rateInForce = signal<number | null>(null);

  /** The date that rate is quoted for. Shown on the panel, because a backdated invoice priced at a rate the
   *  clerk assumed was today's is a difference nobody can see afterwards. A new document falls back to today
   *  until it is dated. */
  readonly rateAsOfDate = signal<Date>(new Date());

  /** A lookup is in flight, so the reload control can say so rather than look inert. */
  readonly rateLoading = signal(false);

  /** The table holds nothing on or before the document's date. This is the reload control's whole reason for
   *  existing: finance publishes the missing row while the clerk has the document open, and refetching beats
   *  losing the form to a page reload. */
  readonly rateUnavailable = signal(false);

  /** The override stays out of the way until it is asked for — the same idiom the payment term above uses
   *  (#4785), which is what makes the panel readable for the documents nobody overrides. */
  readonly rateOverrideOn = signal(false);

  /**
   * #4785 — the exchange rate the SOURCE document (Order Invoice / Receipt) was struck at. When registering
   * from a pre-invoice the API takes this rate over the day's rate (#4306/#4767), so it is shown read-only
   * beneath the daily rate rather than left invisible. Null when not registering from a source.
   */
  readonly sourceExchangeRate = signal<number | null>(null);

  /** Which source the rate above came from, for its label. */
  readonly sourceRateOrigin = signal<'invoice' | 'receipt' | null>(null);

  /**
   * The rate a SAVED document was booked at (rateToUsd), with the source the API resolved it from. Stamped
   * once at registration, so it is what the panel shows whenever the document is reopened — today, tomorrow
   * or in a month. Null while the document is new, where there is nothing booked yet to show.
   */
  readonly bookedRateToUsd = signal<number | null>(null);
  readonly bookedRateSource = signal<ApRateSource | null>(null);

  /** The band this currency plausibly trades in, or null where none is known (nothing is enforced then). */
  readonly rateBounds = computed(() => exchangeRateBoundsFor(this.selectedCurrency()));

  /** True once what is typed is outside that band — a slipped decimal, almost always. */
  readonly rateOutOfBounds = computed(() => !isPlausibleExchangeRate(this.selectedCurrency(), this.typedRate()));

  /** The currency the books are kept in. Hardcoded as USD like every other functional-currency decision
   *  in this module — every company on file keeps its books in dollars. */
  private readonly functionalCurrency = Currency.USD;

  /**
   * #5221 — whether the exchange block has anything to say at all.
   *
   * <p>A document already in the functional currency converts at 1 <i>by definition</i>: the API returns
   * before it ever consults the rate table (<c>ApDocumentService.ResolveExchangeRateAsync</c>), stamps a
   * rate of 1, and sets the functional amount to the total. So the panel was showing "Rate booked on the
   * document: 1.0000" over a functional amount that repeated the total — three readouts that carry no
   * information and invite the clerk to wonder which rate applied, when the answer is that none did.</p>
   *
   * <p>Surfaced on a Receipt (#5232), but the panel is shared by every AP document type, so hiding it here
   * fixes all of them at once.</p>
   */
  readonly showExchangeDetails = computed(() => this.selectedCurrency() !== this.functionalCurrency);

  /**
   * Whether a rate may be typed at all.
   *
   * <p>Only on a document that is not in the functional currency — a USD document converts at 1 by
   * definition — and only while it is still editable, since the rate is stamped at registration and a
   * posted document's rate is history rather than a field.</p>
   */
  readonly canOverrideRate = computed(() => this.showExchangeDetails() && this.isDraft());

  /**
   * The rate that applies: what the user just typed, else what the document was booked at, else the source
   * document's, else the day's.
   *
   * <p>The typed rate leads because the API puts it there — `ApDocumentService.ResolveExchangeRateAsync`
   * takes a manually entered rate over every other source (#5135), all of which are inferences. Leaving it
   * out meant the one number the clerk is actively changing was the one the panel ignored: they typed
   * 26.980793, the functional amount sat at the day's rate, and nothing on screen moved until they saved.</p>
   */
  readonly effectiveRate = computed(() => this.typedRate() ?? this.bookedRateToUsd() ?? this.sourceExchangeRate() ?? this.rateInForce());

  /** True once the document carries its own booked rate — the day's rate is then irrelevant and hidden. */
  readonly hasBookedRate = computed(() => this.bookedRateToUsd() !== null);

  /**
   * Names the rate the panel is actually showing — which of the four possible sources it came from.
   *
   * <p>Follows the same precedence the API resolves by, because that is what decides which one applies:
   * a typed rate, else what the document was booked at, else the source document's, else the table's. The
   * panel used to stack these as separate rows, which read as several competing rates when only ever one
   * of them is in force.</p>
   */
  get rateOriginLabel(): string {
    if (this.typedRate() !== null) return this.t('ApDocument_Rate_Typed', 'Rate you entered (to USD)');
    if (this.hasBookedRate()) return this.bookedRateLabel;
    if (this.sourceExchangeRate() !== null) return this.sourceRateLabel;
    return this.t('ApDocument_Rate_FromTable', 'Rate on file (to USD)');
  }

  /** Names where the displayed rate came from, so the number is never ambiguous. */
  get bookedRateLabel(): string {
    switch (this.bookedRateSource()) {
      case ApRateSource.SourceDocument:
        return this.sourceRateOrigin() === 'receipt'
          ? this.t('ApDocument_BookedRate_Receipt', 'Rate from receipt (to USD)')
          : this.t('ApDocument_BookedRate_Invoice', 'Rate from order invoice (to USD)');
      case ApRateSource.PurchaseOrder:
        return this.t('ApDocument_BookedRate_Po', 'Rate from purchase order (to USD)');
      case ApRateSource.DerivedFromAmounts:
        return this.t('ApDocument_BookedRate_Derived', 'Rate derived from amounts (to USD)');
      // Worth naming rather than folding into the default: a rate somebody typed is the one an auditor
      // will want to ask about, and it should not read as though the system chose it.
      case ApRateSource.Manual:
        return this.t('ApDocument_BookedRate_Manual', 'Rate entered manually (to USD)');
      default:
        return this.t('ApDocument_BookedRate_Daily', 'Rate booked on the document (to USD)');
    }
  }

  get sourceRateLabel(): string {
    return this.sourceRateOrigin() === 'receipt'
      ? this.t('ApDocument_SourceRate_Receipt', 'Rate from receipt')
      : this.t('ApDocument_SourceRate_Invoice', 'Rate from purchase order');
  }

  // Informational summary (backend computes the authoritative amounts)
  /** Every line's total — matches the API's SubTotal, which also includes the charge/fee lines. */
  subtotal = signal(0);

  /** #4785 — the charge/fee portion of `subtotal`, net of any tax embedded in it. */
  readonly feesTotal = signal(0);

  /** #4785 — tax already embedded in the tax-inclusive line totals (#4723), extracted for the summary. */
  readonly embeddedTax = signal(0);

  /** ApDocument.TaxAmount as returned by the API — the recoverable ISV it extracted and posted. Mirrors
   *  `embeddedTax` once saved; kept as the server's own figure rather than folded into the summary. */
  readonly documentTaxAmount = signal(0);

  /**
   * Tax shown in the summary. This used to add `documentTaxAmount`, which was safe only while the API
   * forced it to 0 — now that the API extracts and posts the same tax, adding both would double the row.
   * `embeddedTax` is the live figure and uses the API's exact formula, so it tracks the form as it is
   * edited and agrees with what posts.
   */
  readonly taxTotal = computed(() => this.embeddedTax());

  /** Goods/services, net of tax and with the fees on their own row — so the parts add up to the total. */
  readonly itemsSubtotal = computed(() => Math.round((this.subtotal() - this.embeddedTax() - this.feesTotal()) * 100) / 100);

  /**
   * What the document is worth — equals the API's TotalAmount, which stays GROSS. The tax is inside the
   * line totals, so nothing is added on top: extracting it changes which GL account it lands in, not what
   * the provider is owed.
   */
  readonly grandTotal = computed(() => this.subtotal());

  // Withholding (#4454): the decision moved OUT of invoice registration into the separate Constancia de
  // Retención screen (ap-withholding-details) — the Financial Officer posts it before payment. Invoices
  // no longer capture or persist withholding here.

  currencyOptions = signal<EnumOption[]>([]);
  private currencyLabels: Record<number, string> = {};
  private statusLabels: Record<number, string> = {};
  private uoms: Record<number, string> = {};

  private readonly documentTypeSig = signal<number>(ApDocumentType.Invoice);
  /** Invoice lines use the item type-ahead; other document kinds use a free description. */
  readonly useItemTypeAhead = computed(() => this.documentTypeSig() === ApDocumentType.Invoice);
  /** Amount-only kinds (#4462): a line is description + amount — the qty column is hidden and the
   * API forces Quantity = 1 (amount travels in unitPrice), so nothing downstream changes shape. */
  readonly isAmountOnly = computed(() => AMOUNT_ONLY_AP_DOCUMENT_TYPES.includes(this.documentTypeSig()));
  /** Receipt (#4457): relabels the fiscal number field ("Receipt #") and the header title. */
  readonly isReceipt = computed(() => this.documentTypeSig() === ApDocumentType.Receipt);
  /** Purchase Receipt (#4467): self-issued fiscal number (drawn on Post — the form shows a
   * read-only preview), optional one-time vendor, user-selected CxP account. */
  readonly isPurchaseReceipt = computed(() => this.documentTypeSig() === ApDocumentType.PurchaseReceipt);
  /** Policy / Póliza (#6016): Purchase Receipt lines, but the number is the insurer's policy number (typed,
   * required) and there is never a CAI — not the provider's and not ours. */
  readonly isPolicy = computed(() => this.documentTypeSig() === ApDocumentType.Policy);
  /** Receipt, Purchase Receipt and Policy let the user search the item master and add an item-based line —
   * in addition to free-text description lines. (The Invoice uses the PO-driven picker instead; notes are
   * seeded from their source invoice.) */
  readonly allowItemSearch = computed(() => this.isReceipt() || this.isPurchaseReceipt() || this.isPolicy());
  /** True when a line can carry an item master: the Invoice's PO lines, or a searched item on a Receipt /
   * Purchase Receipt. Gates the "reload item masters" action. (#4785 merged the Item Code column into the
   * Item column, where the code renders beneath the description.) */
  readonly hasItemLines = computed(() => this.useItemTypeAhead() || this.allowItemSearch());
  /** Debit/Credit Notes (#4473/#4477): seeded from a picked invoice; amount per line. */
  readonly isDebitNote = computed(() => this.documentTypeSig() === ApDocumentType.DebitNote);
  readonly isCreditNote = computed(() => this.documentTypeSig() === ApDocumentType.CreditNote);
  readonly isNote = computed(() => NOTE_AP_DOCUMENT_TYPES.includes(this.documentTypeSig()));
  /** Self-issued fiscal documents (#4526): PurchaseReceipt + Debit/Credit Notes draw their own
   * fiscal number + CAI on Post from the company's own CAI range — the client sends neither and the
   * form shows no #/CAI inputs while drafting. Provider (incoming) types (Invoice, Receipt) still
   * carry a provider-supplied # and CAI. */
  /** Numbered from OUR range, so the fiscal number and CAI are stamped on post rather than entered.
   *  Notes left this set in #4791: an AP note is one the provider issues us, so it carries their CAI. */
  readonly isSelfIssued = computed(() => isSelfIssuedApDocument(this.documentTypeSig()));
  /** Credit notes reduce the payable (shown as −); debit notes add (+). */
  readonly summarySign = computed(() => (this.isCreditNote() ? -1 : 1));

  // ---- Credit / Debit Note by reason (#6324) ----
  readonly ApNoteReason = ApNoteReason;
  readonly ApDocumentType = ApDocumentType;
  readonly ApNotePriceMode = ApNotePriceMode;
  /** The note's Motivo — decides the line columns and how the API derives every line. */
  readonly noteReason = signal<ApNoteReason | null>(null);
  /** Financiera: no original document; the user picks the provider and each line's account. */
  readonly isFinancialNote = computed(() => this.isNote() && this.noteReason() === ApNoteReason.Financial);
  readonly noteReasonOptions = signal<EnumOption[]>([]);
  readonly notePriceModeOptions = signal<EnumOption[]>([]);
  readonly noteCauseOptions = signal<EnumOption[]>([]);
  private noteCauseLabels: Record<number, string> = {};
  /** Impuesto: the rates a correction can land on. */
  readonly noteTaxRateOptions: EnumOption[] = [
    { label: '0 %', value: 0 },
    { label: '15 %', value: 0.15 },
    { label: '18 %', value: 0.18 },
  ];
  /** The original Invoice/Receipt — picked on a new note, read back on a saved one. Its balance is what the
   *  summary shows before and after. */
  readonly noteOriginal = signal<ApDocument | null>(null);
  /** Some line already carries an adjustment — from then on the Motivo is locked: changing it would change
   *  what every entered figure means. */
  readonly noteHasContent = signal(false);
  readonly noteReasonLocked = computed(() => !this.isDraft() || this.noteHasContent());
  /** P14 warning: the original already has a posted Constancia, which this note will not adjust. */
  readonly noteOriginalHasPostedCertificate = signal(false);
  /** P13 warning: a credit note on service lines dated in another month than its invoice. */
  readonly noteServiceMonthMismatch = signal(false);
  /** How a credit note will split on posting: settled on its original, the rest left as provider credit. */
  readonly noteCreditSplit = computed(() =>
    this.isCreditNote()
      ? previewCreditSplit(this.grandTotal(), this.isFinancialNote() ? null : (this.noteOriginal()?.balanceDue ?? null))
      : null,
  );

  /** #4726 / #4791 UI complement — a fiscal authorization applies only where the provider's domicile has a
   * regime that issues one. When the selected provider's domicile is known and its country issues none, the
   * authorization field is hidden and the provider invoice number is accepted as a free number. Unknown
   * country falls back to showing the field (safe default). */
  readonly providerCountryHasNoAuthorization = computed(() => {
    const provider = this.selectedProvider();
    if (!provider) return false;
    const country = provider.countryCode;
    // Show the field only when the domicile is a country that issues authorizations. A known other country
    // always hides it; an unset/blank country hides it while authoring a new document (a loaded document
    // keeps its saved code, since the rebuilt provider carries no country).
    if (country && AUTHORIZATION_COUNTRY_OPTIONS.some((c) => c.value === country)) return false;
    if (country) return true;
    return this.isNew();
  });

  /**
   * #4890 — whether the clerk must supply the fiscal document number. It is the number printed on the
   * provider's paper, and a document quoting a CAI is range-checked against it on Post, so a draft saved
   * with the field blank only failed much later as an "outside the authorized range" rejection on a number
   * nobody ever entered.
   *
   * Required for every kind but the Purchase Receipt, which draws its number from OUR range on Post and so
   * has nothing to type. This deliberately no longer exempts providers outside an authorization regime
   * (#4726/#4791 did): a foreign invoice still has a number printed on it, and posting without one left a
   * payable nothing could be reconciled against. Mirrors EnsureFiscalNumberBeforePost on the API, which is
   * what actually enforces it — this only decides whether the clerk sees the error inline or on save
   * (#5029).
   */
  readonly requiresFiscalNumber = computed(() => !this.isSelfIssued());

  /**
   * Whether the clerk must pick a CAI (#5029). Required for every provider-issued kind — Invoice, Receipt
   * and, now that they are no longer treated as self-issued, Credit and Debit Notes — whenever the
   * provider's domicile runs an authorization regime.
   *
   * <p>The country gate lives here rather than on the fiscal number, which is where it used to be: a
   * foreign provider still prints a number on their invoice, but they genuinely have no CAI, so requiring
   * one would demand something that does not exist. Purchase Receipts are excluded because their CAI is
   * ours, stamped on post.</p>
   *
   * <p>The options themselves are already narrowed to authorizations covering this document type
   * (loadProviderCais, #4791), so a required field can only be satisfied by a CAI that actually authorizes
   * the kind being created — a provider's invoice-only CAI simply does not appear on a Credit Note.</p>
   */
  readonly requiresCai = computed(() => this.takesCai() && !this.providerCountryHasNoAuthorization());

  /** Whether this kind carries a CAI at all. A Policy never does (#6016); a self-issued kind carries ours,
   * stamped on post. */
  readonly takesCai = computed(() => !this.isSelfIssued() && !this.isPolicy());

  /** This document's full payable picture — its notes + withholding + net (#4516). Fetched for a posted base
   * document so its detail page shows the adjusted state without a DB round-trip. */
  readonly payableDetail = signal<PayableDocument | null>(null);
  /** Show the resumen only when something actually adjusts the base (a note, withholding, or a partial payment). */
  readonly showResumen = computed(() => {
    const p = this.payableDetail();
    return !!p && (p.hasAdjustments || (p.document.paidAmount ?? 0) > 0);
  });

  // ---- Accounting / GL impact (#5024 #5031) ----
  /** The journal entry this document booked when it posted. Null until then — a draft has no entry. */
  readonly journalEntryReference = signal<string | null>(null);
  readonly journalEntryDocumentNumber = signal<string | null>(null);
  /** The summary is supplementary: show the panel only once there is an entry to show. */
  readonly showAccounting = computed(() => !!this.journalEntryReference());
  /** The component takes a list — an AP document books exactly one entry, so this is always one item. */
  readonly journalEntryReferences = computed(() => {
    const reference = this.journalEntryReference();
    return reference ? [reference] : [];
  });
  /**
   * Bumped every time the document is (re)loaded, to make the GL summary refetch the entry it is showing.
   *
   * <p>Cancelling reverses the entry in place and KEEPS <c>journalEntryReference</c> — the reversal is
   * stamped beside it rather than over it — so a reload hands the summary the same id it already has, and
   * it would otherwise skip the fetch and go on showing a Posted badge on an entry that is now Reversed.
   * The id cannot express "same entry, different status"; this can.</p>
   */
  readonly journalEntryReloadToken = signal(0);

  // ---- Debit/Credit Note (#4473/#4477) ----
  /** The posted invoice this note adjusts — drives the provider, currency and seeded lines. */
  selectedSourceInvoice = signal<ApDocument | null>(null);
  /** Source-invoice number badge shown on a loaded note. */
  noteSourceNumber = signal<string | null>(null);
  /** The source invoice's id as SAVED on the note. The picker only exists while the note is new (afterwards
   * the source shows as a badge), so `selectedSourceInvoice` is empty on a reopened note and the payload has
   * to echo the stored link back — the API re-validates it on every edit and refuses a note that references
   * no invoice. */
  noteSourceReference = signal<string | null>(null);

  // ---- Purchase Receipt (#4467) ----
  /** "Proveedor ocasional": the vendor has no master record — name is free text and the CxP
   * account is user-selected (#4466). */
  /** Expected-next-number preview (#4465) — informational; the real draw happens on Post. */
  purchaseReceiptPreview = signal<PurchaseReceiptNextNumber | null>(null);
  /** Bilingual API message when no active Purchase Receipt CAI range is configured for the company. */
  purchaseReceiptPreviewError = signal<string | null>(null);
  readonly isDraft = computed(() => this.status() === ApDocumentStatus.Draft);
  readonly isPersisted = computed(() => !this.isNew() && !!this.currentId());

  /**
   * Withdrawing a POSTED document is reserved for the FinanceTransactionReversal support role — the same
   * authority that returns a posted AP payment to draft, and for the same reason: what it gives back is a
   * ledger entry, not a form someone mistyped.
   *
   * <p>Deleting a draft deliberately carries no extra role — a draft has posted nothing, so removing it is
   * the same authority as editing it. This is the opposite case.</p>
   *
   * <p>Hiding the button is a courtesy, not the enforcement: the API carries the same rule, and it has to,
   * because a role only the client checks is a suggestion.</p>
   */
  readonly canCancel = this.auth.roles.includes(SupportRoles.FinanceTransactionReversal);

  /**
   * Offered on a POSTED document only.
   *
   * <p>Everything past Posted has been settled by something — cash, an advance or a Constancia — and the API
   * refuses those by naming the record that has to be undone first. Showing the button there would offer an
   * action that can only be refused, so the screen stops at the one status where it can succeed.</p>
   */
  readonly canOfferCancel = computed(() => this.canCancel && this.isPersisted() && this.status() === ApDocumentStatus.Posted);

  /** #6324 — a posted Invoice or Receipt can take Credit and Debit Notes, paid ones included. */
  readonly canIssueNote = computed(
    () =>
      this.isPersisted() &&
      (this.documentTypeSig() === ApDocumentType.Invoice || this.isReceipt()) &&
      [ApDocumentStatus.Posted, ApDocumentStatus.PartiallyPaid, ApDocumentStatus.Paid].includes(this.status()),
  );

  /** Opens a new note with this document already picked as its original. */
  issueNote(type: 'CREDITNOTE' | 'DEBITNOTE'): void {
    this.router.navigate(['/ap-document-details'], { queryParams: { id: 'new', type, original: this.currentId() } });
  }

  // The reason is optional: the status already records that the document was withdrawn, and who and when.
  // This records WHY, which is the one thing a cancelled fiscal number cannot say for itself — worth asking
  // for, not worth blocking on.
  cancelDialogVisible = signal<boolean>(false);
  cancelSaving = signal<boolean>(false);
  readonly cancelReason = new FormControl<string>('', { nonNullable: true });

  /**
   * Supporting files on the document — the provider's scanned invoice, a delivery note, an email
   * authorising a charge.
   *
   * <p>Held apart from the form because they are not form data: each add and remove is its own request
   * that returns the updated document, so there is nothing here for Save to submit. Available in every
   * status — an attachment is evidence ABOUT the document, and the scan routinely arrives after posting —
   * but only once the document EXISTS, since a file has to be attached to something.</p>
   */
  readonly attachments = signal<S3Object[]>([]);
  readonly canManageAttachments = computed(() => this.isPersisted());

  // ---- received date (#5165) ----

  /** Distinct days something was received against this document's POs, as the server computed them.
   * Display order belongs to `receivedDateOptionItems`, not to this. */
  readonly receivedDateOptions = signal<Date[]>([]);
  /** True once the options call has answered, so "no dates" can be told apart from "not asked yet". */
  private readonly receivedDateOptionsLoaded = signal(false);
  /** The PO set the current options were fetched for, so line edits don't re-ask for the same answer. */
  private readonly loadedReceivedDatePoSignature = signal<string | null>(null);

  /**
   * How the received date is captured, which differs by where the date can be KNOWN from:
   *
   * - `shipment` — registered from an Order Invoice. The invoice belongs to one shipment and that shipment
   *   records the day it was completed, so the API owns the date and the field is read-only.
   * - `grn` — an ordinary Invoice with receiving dates available: pick one of the days its Goods Receipt
   *   Notes were booked.
   * - `manual` — everything else. An Invoice whose POs have nothing receipted yet (still required — the
   *   clerk states a date rather than being blocked from registering), or a Receipt / PurchaseReceipt, which
   *   has no receiving event at all and leaves the field optional.
   */
  readonly receivedDateMode = computed<'shipment' | 'grn' | 'manual'>(() => {
    if (this.isPreInvoice() && !this.loadedFromReceipt()) return 'shipment';
    if (this.documentTypeSig() === ApDocumentType.Invoice && this.receivedDateOptions().length > 0) return 'grn';
    return 'manual';
  });

  /**
   * Only an Invoice has a receiving event the payment clock must run from — except one registered from an
   * Order Invoice, whose date belongs to the shipment and cannot be typed here at all. Requiring it there
   * would block registering an invoice for goods still in transit, which is the ordinary case: the provider
   * bills at dispatch. The API applies the same exemption; the date arrives when the shipment completes.
   */
  readonly receivedDateRequired = computed(
    () => this.documentTypeSig() === ApDocumentType.Invoice && this.receivedDateMode() !== 'shipment',
  );

  /** Shipment-owned but not yet received — the field is legitimately empty and nobody can fill it. */
  readonly receivedDatePendingShipment = computed(() => this.receivedDateMode() === 'shipment' && !this.form.controls.receivedDate.value);

  /** Shown when an Invoice has no GRN dates to offer, so the empty picker is explained rather than puzzling. */
  readonly receivedDateNoOptionsHint = computed(
    () =>
      this.receivedDateRequired() &&
      this.receivedDateOptionsLoaded() &&
      this.receivedDateOptions().length === 0 &&
      this.receivedDateMode() === 'manual',
  );

  /**
   * The rows the clerk picks from, most recent day first: the receipt being invoiced is nearly always the
   * latest one, so it should not be at the bottom of a long list.
   *
   * Sorted here rather than taken on trust from the endpoint. `p-select` renders the array exactly as
   * given, and display order is a screen decision — one that should not quietly change if the endpoint's
   * ordering ever does.
   */
  readonly receivedDateOptionItems = computed(() =>
    // formatApiDate, not formatDate: these are API-supplied calendar dates, not picker values (#5234).
    [...this.receivedDateOptions()]
      .sort((a, b) => b.getTime() - a.getTime())
      .map((date) => ({ label: this.formatApiDate(date), value: date })),
  );

  // ---- withholding requirement (#4842) ----
  /** Whether the SAVED document needs a Constancia de Retención. Server-derived from the line
   * classifications — deliberately not recomputed here, since the classification → service mapping is API
   * configuration and a second client-side copy of that rule would eventually disagree with it. */
  requiresWithholding = signal<boolean>(true);
  withholdingWaivedReason = signal<string | null>(null);
  withholdingWaivedBy = signal<string | null>(null);
  /** The API's classification → service rule, fetched once. Never hard-coded here: a second copy of the rule
   * would drift and the form would mis-state whether a certificate is needed. */
  private readonly withholdingRules = signal<WithholdingClassificationRules | null>(null);
  /** Bumped on every line add/remove/classification change so the live preview below recomputes — the lines
   * live in a FormArray, which signals can't observe directly. */
  private readonly lineClassificationVersion = signal(0);
  /**
   * Removing a requirement — waiving it, or undoing a forced one — is refused once any certificate cites the
   * document, because the certificate's lines exist BECAUSE the document required them. The API refuses this
   * too; checking here means the clerk sees why the button is gone instead of clicking into a rejection.
   */
  /**
   * Draft AND Posted, in both directions (#5166) — the whole withholding workflow happens after posting.
   * Certificates are built against posted documents, and a posted document that wrongly requires one is
   * blocked by the payment gate until somebody removes the requirement. Stops once money has moved:
   * withholding reduces the payable before it is paid, and afterwards the correction is a credit note.
   */
  private readonly canChangeRequirement = computed(
    () => this.isPersisted() && (this.status() === ApDocumentStatus.Draft || this.status() === ApDocumentStatus.Posted),
  );

  readonly canWaiveWithholding = computed(
    () => this.canChangeRequirement() && this.requiresWithholding() && this.citingCertificateCount() === 0,
  );

  /** Waived: the clerk asserted no certificate is needed. The reason is what distinguishes this from a
   * document that never needed one — the API writes a mandatory reason on a waiver and clears it whenever it
   * re-derives the requirement, so `!requiresWithholding` alone conflates the two and labels a goods-only
   * document "waived" with an empty reason. */
  readonly isWithholdingWaived = computed(() => this.isPersisted() && !this.requiresWithholding() && !!this.withholdingWaivedReason());
  /** Not required at all — the classifications derived no requirement and nobody overrode it. Distinct from
   *  waived, which is somebody's recorded decision. */
  readonly withholdingNotRequired = computed(() => this.isPersisted() && !this.requiresWithholding() && !this.withholdingWaivedReason());

  // ---- forced withholding (#5166) ----

  /** Deploy-time switch. Off means the control is not rendered; the API gates the capability independently. */
  readonly forcedWithholdingEnabled = environment.features?.forcedWithholding ?? false;
  /** Derived server-side from the presence of a forced reason — there is one requirement flag, and the
   *  reasons say how it got its value. */
  withholdingForced = signal<boolean>(false);
  withholdingForcedReason = signal<string | null>(null);
  withholdingForcedBy = signal<string | null>(null);
  /** Certificates citing this document — what makes the requirement impossible to remove (#5166). */
  private readonly citingCertificateCount = signal(0);

  /** The false → true direction, offered only where the derived rule has said no certificate is needed. */
  readonly canRequireWithholding = computed(
    () => this.forcedWithholdingEnabled && this.canChangeRequirement() && !this.requiresWithholding(),
  );

  /** Explains a missing waive button rather than leaving the panel looking broken. */
  readonly withholdingLockedReason = computed(() =>
    this.requiresWithholding() && this.canChangeRequirement() && this.citingCertificateCount() > 0
      ? this.t(
          'ApDocument_Withholding_Locked',
          'A withholding certificate already covers this document, so its withholding requirement can no longer be removed.',
        )
      : null,
  );

  /**
   * #4842 — live preview of the requirement the API will derive on save, from the classifications currently
   * on the form. The stored value is always the API's; this only avoids the clerk being surprised by it.
   *
   * <p>Deliberately says nothing about WHY (#5166): the classification rule is the API's default starting
   * point, not the whole truth about what gets withheld — goods are withheld too, and a clerk can require a
   * certificate on any document. Wording that told the clerk "services are what we withhold" would be
   * teaching them something false.</p>
   */
  readonly willRequireWithholding = computed(() => {
    this.lineClassificationVersion();
    const rules = this.withholdingRules();
    if (!rules || rules.treatAllAsService) return true;
    const codes = this.lines.controls.map((line) => line.getRawValue().itemClassificationCode);
    if (codes.length === 0) return true;
    const services = new Set(rules.serviceClassificationCodes.map((code) => code.toUpperCase()));
    return codes.some((code) => !code?.trim() || services.has(code.trim().toUpperCase()));
  });
  // #4842/#5166 — ONE dialog for both directions, because there is one requirement flag. `withholdingIntent`
  // is the value being asserted, and the dialog's copy and confirm label follow from it.
  withholdingDialogVisible = signal<boolean>(false);
  withholdingSaving = signal<boolean>(false);
  withholdingIntent = signal<boolean>(false);
  readonly withholdingReason = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  /** A document registered from a Pre-Invoice (#4306) — during creation or after load. Its seeded lines are a
   * read-only preview: they can't be removed, qty/price are locked; only the per-line GL account is editable.
   * Charges added to an Order-Invoice-sourced one (#6537) are the clerk's, as on a plain Invoice. */
  readonly isPreInvoice = computed(() => this.usePreInvoice() || !!this.preInvoiceNumber());
  /** #4767 — a pre-invoice drawn from an Order Receipt (being created, or loaded). Its lines carry no PO and ARE
   * the document's lines, so it has no Charges / Fees section. */
  readonly isReceiptPreInvoice = computed(
    () =>
      this.loadedFromReceipt() || (this.usePreInvoice() && (this.preInvoiceSource() === 'receipt' || !!this.selectedPreInvoice()?.receipt)),
  );
  /** #4725/#6537 — PO-driven Invoices keep their non-PO lines in a Charges / Fees section, including one
   * registered from an Order Invoice; only a receipt-sourced pre-invoice has none. */
  readonly allowsCharges = computed(() => this.useItemTypeAhead() && !this.isReceiptPreInvoice());
  /** #4767 — badge text for a loaded document, naming the Order document it was registered from. */
  readonly preInvoiceBadgeLabel = computed(() =>
    this.loadedFromReceipt() ? this.t('ApDocument_PreInvoice_Source_Receipt', 'Receipt') : this.t('ApDocument_PreInvoice', 'Pre-Invoice'),
  );

  readonly form: FormGroup<ApDocumentFormGroup> = this.formBuilder.group<ApDocumentFormGroup>({
    documentType: new FormControl<ApDocumentType>(ApDocumentType.Invoice, { nonNullable: true }),
    noteReason: new FormControl<ApNoteReason | null>(null),
    noteCause: new FormControl<ApNoteCause | null>(null),
    provider: new FormControl<Business | null>(null, [Validators.required]),
    documentDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    // #5165 — required only for an Invoice; the validator is applied reactively in
    // applyReceivedDateRules(), because the document kind can change while the form is open.
    receivedDate: new FormControl<Date | null>(null),
    postingDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    fiscalNumber: new FormControl<string | null>(null),
    providerReferenceNumber: new FormControl<string | null>(null),
    cai: new FormControl<string | null>(null),
    dueDate: new FormControl<Date | null>(null),
    // #4784 — display only. Always disabled: the term is resolved API-side from the source document and is
    // never sent back in the payload. Bound to the master-data dropdown purely to render its friendly name.
    paymentTermCode: new FormControl<string | null>(null),
    // USD: most AP documents are import purchases, so it is the likelier answer than the local currency.
    // Only a default — every sourced path (order invoice, receipt, first PO) overwrites it and locks it.
    currency: new FormControl<Currency>(Currency.USD, { nonNullable: true, validators: [Validators.required] }),
    functionalAmount: new FormControl<number | null>(null),
    rateToUsd: new FormControl<number | null>(null),
    notes: new FormControl<string | null>(null),
    lines: this.formBuilder.array<FormGroup<ApDocumentLineFormGroup>>([]),
  });

  /** Live value of the override field, so the bounds hint and the warning react as it is typed. */
  private readonly typedRate = toSignal(this.form.controls.rateToUsd.valueChanges, { initialValue: null });

  get lines(): FormArray<FormGroup<ApDocumentLineFormGroup>> {
    return this.form.controls.lines;
  }

  private readonly fieldLabels: Record<string, string> = {
    provider: 'ApDocument_Provider',
    documentDate: 'ApDocument_DocumentDate',
    receivedDate: 'ApDocument_ReceivedDate',
    postingDate: 'ApDocument_PostingDate',
    currency: 'Currency',
    purchaseOrder: 'ApDocument_PurchaseOrder',
    lines: 'ApDocument_Lines',
    fiscalNumber: 'ApDocument_FiscalInvoiceNumber',
  };

  constructor() {
    this.currencyOptions.set(this.enumToOptions('Currency'));
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.statusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
    this.noteReasonOptions.set(this.enumToOptions('ApNoteReason'));
    this.notePriceModeOptions.set(this.enumToOptions('ApNotePriceMode'));
    this.noteCauseLabels = this.enumService.getEnum('ApNoteCause').values as Record<number, string>;

    // UnitOfMeasure lives in Item.API enums; the EnumService loads + caches them (source 'item').
    this.uoms = this.enumService.getEnum('UnitOfMeasure', 'item').values as Record<number, string>;

    this.form.controls.documentType.valueChanges.subscribe((v) => {
      this.documentTypeSig.set(v ?? ApDocumentType.Invoice);
      // #5165 — the received date is required on an Invoice and optional elsewhere, so changing the kind
      // has to add or drop the requirement rather than leave a stale one on an untouchable control.
      this.loadedReceivedDatePoSignature.set(null);
      this.loadReceivedDateOptions();
      this.applyReceivedDateRules();
      // A CAI is registered per document type (#4791), so the offered set changes with the type — not just
      // with the provider. Without this the dropdown keeps the previous type's CAIs and the one on screen
      // is rejected at post time. Routed through syncProviderCais so the domicile rule is applied the same
      // way here as on a provider change — reloading directly would repopulate the dropdown for a provider
      // whose country issues no authorizations.
      this.syncProviderCais(this.selectedProvider());
    });
    this.form.controls.documentDate.valueChanges.subscribe(() => {
      this.recomputeDueDate();
      // The ordering rule compares against this date, so moving it can invalidate a received date that was
      // valid when it was picked.
      this.form.controls.receivedDate.updateValueAndValidity({ emitEvent: false });
    });
    this.form.controls.currency.valueChanges.subscribe((c) => {
      const currency = c ?? Currency.USD;
      this.selectedCurrency.set(currency);
      this.loadRateInForce(c);
      // #5221 — switching to the books' own currency retires the override along with its field. The API
      // ignores it there (a same-currency document returns before the manual branch), so leaving it in the
      // payload would claim a rate that was never applied.
      if (currency === this.functionalCurrency) this.form.controls.rateToUsd.setValue(null);
    });
    // #4785 — an overridden term re-derives credit days (from the API catalog) and the due date.
    this.form.controls.paymentTermCode.valueChanges.subscribe((code) => {
      this.paymentTermCode.set(code ?? null);
      // #6324 — a debit note's term starts as its original's, so that is what an override departs from.
      const original = this.isDebitNote() ? this.noteOriginal() : null;
      const inherited = original ? (original.paymentTermCode ?? null) : this.providerPaymentTermCode();
      const inheritedSource = original ? PaymentTermSource.RelatedDocument : PaymentTermSource.ProviderDefault;
      this.paymentTermSource.set(code && code !== inherited ? PaymentTermSource.Manual : inheritedSource);
      this.resolvedCreditDays.set(code ? (this.creditDaysByTerm().get(code) ?? null) : null);
      this.recomputeDueDate();
    });
    this.lines.valueChanges.subscribe(() => this.recomputeTotals());
    // PO picker search hits Order.API (debounced) so a PO beyond the first page is found, not just the
    // preloaded set; the instant local filter (filteredPos) narrows what's already loaded meanwhile.
    this.poSearchControl.valueChanges.pipe(debounceTime(PO_SEARCH_DEBOUNCE_MS), distinctUntilChanged()).subscribe((text) => {
      const providerId = this.selectedProvider()?.id;
      if (this.useItemTypeAhead() && providerId) this.loadProviderPurchaseOrders(providerId, text);
    });
    this.form.controls.postingDate.valueChanges.subscribe((d) => this.checkPostingDateDeviation(d));
    this.form.controls.documentDate.valueChanges.subscribe((d) => {
      this.checkInvoiceAge(d);
      // The rate belongs to the document's date, so moving the date re-prices the document.
      this.loadRateInForce(this.form.controls.currency.value);
    });
    this.form.controls.functionalAmount.disable({ emitEvent: false });
    // Due date is derived (documentDate + resolved credit days), so it is read-only.
    this.form.controls.dueDate.disable({ emitEvent: false });
    // #4784 — the payment term is never editable here; it belongs to the originating PO / Receipt.
    this.form.controls.paymentTermCode.disable({ emitEvent: false });
    // #4890 — the fiscal number's requirement depends on the document type AND the selected provider's
    // domicile, neither of which is known when the form is built, and both of which change afterwards. An
    // effect re-applies it whenever either moves, so the rule can't go stale the way a build-time validator
    // would. setValidators (not addValidators) because this control carries no other validator and the
    // requirement has to be removable again when the type or provider changes back.
    effect(() => {
      const control = this.form.controls.fiscalNumber;
      control.setValidators(this.requiresFiscalNumber() ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    });

    // #5029 — same shape for the CAI, and for the same reason: the requirement depends on the document type
    // and the provider's domicile, both of which change after the form is built. Kept as its own effect
    // because the two rules differ — the fiscal number is required for every provider-issued kind, the CAI
    // only when the provider's country issues one.
    effect(() => {
      const control = this.form.controls.cai;
      control.setValidators(this.requiresCai() ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => this.breadcrumbService.setCurrent(this.isNew() ? 'Common_New' : this.documentNumber()), { allowSignalWrites: true });
  }

  /**
   * #4714 — advisory posting-date deviation check. Non-blocking: when the posting date deviates from today
   * beyond the company's configured threshold (default 15 days) a p-message is shown near the field; the
   * document can still be saved/posted. The hard gate stays the open-period check on the server.
   */
  private checkPostingDateDeviation(date: Date | null): void {
    const company = this.companyReference;
    if (!date || !company) {
      this.postingDateWarning.set(null);
      return;
    }
    this.accountingPeriodService.checkPostingDateDeviation(company, this.toIso(date)).subscribe({
      next: (res) => this.postingDateWarning.set(res.data?.exceedsThreshold ? (res.data.message ?? null) : null),
      error: () => this.postingDateWarning.set(null),
    });
  }

  /**
   * #4783 — advisory invoice-age check. Non-blocking: when the vendor invoice was issued more than the
   * company's threshold ago (default 60 days) a red p-message is shown under the document date; the
   * document can still be saved and posted. A future issue date never trips it — the API ages past-only.
   */
  private checkInvoiceAge(date: Date | null): void {
    const company = this.companyReference;
    if (!date || !company) {
      this.invoiceAgeWarning.set(null);
      return;
    }
    this.service.checkInvoiceAge(company, this.toIso(date)).subscribe({
      next: (res) => this.invoiceAgeWarning.set(res.data?.exceedsThreshold ? (res.data.message ?? null) : null),
      error: () => this.invoiceAgeWarning.set(null),
    });
  }

  ngOnInit(): void {
    // #4842 — the ItemClassification catalog for description-only lines. Short list, fetched once per form
    // (never per line). Item lines never read it: their classification comes from the item master.
    this.masterDataService.getByCategoryCodes([ITEM_CLASSIFICATION_CATEGORY]).subscribe({
      // Only active entries are offered. A retired classification still shows on documents already carrying
      // it (the control renders the stored code), it just can't be chosen again.
      next: (response) => this.itemClassifications.set((response.data ?? []).filter((entry) => entry.isActive)),
      error: () => this.itemClassifications.set([]),
    });

    // #4842 — the API's service rule, for the live "will need a certificate" preview. On failure the signal
    // stays null and willRequireWithholding reports true, which is the same safe default the API applies.
    this.service.getWithholdingClassificationRules().subscribe({
      next: (response) => {
        this.withholdingRules.set(response.data ?? null);
        // Lines are usually already on the form by now — a loaded document, or a PO expanded while this was
        // in flight. Until the rules landed every line read as a service and nothing was assigned, so this
        // is the first moment the goods lines can be filled in.
        this.applyGlAccountRules();
      },
      error: () => this.withholdingRules.set(null),
    });

    // #4784 — small static catalog, cached in the proxy. Loaded up front so a term picked before it lands
    // still resolves: previewPaymentTerm re-runs on selection, and the term is re-previewed once it arrives.
    this.paymentTermsService.getCreditDaysMap().subscribe({
      next: (map) => {
        this.creditDaysByTerm.set(map);
        const current = this.paymentTermCode();
        if (current) {
          this.resolvedCreditDays.set(map.get(current) ?? null);
          this.recomputeDueDate();
        }
      },
      error: () => this.creditDaysByTerm.set(new Map()),
    });

    const id = this.route.snapshot.queryParamMap.get('id');
    const type = this.route.snapshot.queryParamMap.get('type');
    this.documentTypeSig.set(this.parseType(type));
    this.form.controls.documentType.setValue(this.parseType(type), { emitEvent: false });
    if (this.isReceipt()) this.headerTitle.set(this.t('ApDocument_NewReceipt', 'New Receipt'));
    if (this.isPurchaseReceipt()) this.headerTitle.set(this.t('ApDocument_NewPurchaseReceipt', 'New Purchase Receipt'));
    if (this.isPolicy()) this.headerTitle.set(this.t('ApDocument_NewPolicy', 'New Policy'));
    if (this.isDebitNote()) this.headerTitle.set(this.t('ApDocument_NewDebitNote', 'New Debit Note'));
    if (this.isCreditNote()) this.headerTitle.set(this.t('ApDocument_NewCreditNote', 'New Credit Note'));
    // Notes have no provider picker — the provider comes from the chosen source invoice.
    if (this.isNote()) this.form.controls.provider.clearValidators();

    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadDocument(id);
    } else {
      // Pre-Invoice (#4306): the speed-dial "Pre Invoice" action routes here with source=preinvoice.
      if (this.route.snapshot.queryParamMap.get('source') === 'preinvoice') {
        this.setPreInvoiceMode(true);
      } else if (!this.useItemTypeAhead() && !this.isNote()) {
        // Free-description types seed an empty line; notes seed theirs from the source invoice instead.
        this.addLine();
      }
      this.loadRateInForce(this.form.controls.currency.value);
      // Purchase Receipt (#4465): fetch the expected-next-number preview for the banner.
      if (this.isPurchaseReceipt()) this.loadPurchaseReceiptPreview();
      // #6324 — "Nota de crédito / débito" from an invoice's page opens here with that invoice already picked.
      const originalId = this.route.snapshot.queryParamMap.get('original');
      if (this.isNote() && originalId) {
        this.service.getById(originalId).subscribe({
          next: (response) => {
            if (response.data) this.onSourceInvoiceSelected(response.data);
          },
        });
      }
    }
  }

  // ---- Purchase Receipt (#4467) ----
  private loadPurchaseReceiptPreview(): void {
    this.service.getNextFiscalNumber(ApDocumentType.PurchaseReceipt).subscribe({
      next: (response) => {
        this.purchaseReceiptPreview.set(response.data ?? null);
        this.purchaseReceiptPreviewError.set(null);
      },
      error: (error) => {
        this.purchaseReceiptPreview.set(null);
        // Surface the API's bilingual message (e.g. no active Purchase Receipt CAI range configured).
        this.purchaseReceiptPreviewError.set(
          error?.error?.message ?? this.t('ApDocument_PurchaseReceipt_NoPreview', 'The next fiscal number could not be determined.'),
        );
      },
    });
  }

  // ---- helpers ----
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private parseType(raw: string | null): ApDocumentType {
    // Only kinds whose form flow has shipped are routable; the rest land on Invoice.
    // Receipt: #4457/#4462. Purchase Receipt: #4458/#4467. Debit/Credit Note: #4455/#4456. Policy: #6016.
    switch ((raw ?? '').toUpperCase()) {
      case 'RECEIPT':
        return ApDocumentType.Receipt;
      case 'PURCHASERECEIPT':
        return ApDocumentType.PurchaseReceipt;
      case 'POLICY':
        return ApDocumentType.Policy;
      case 'DEBITNOTE':
        return ApDocumentType.DebitNote;
      case 'CREDITNOTE':
        return ApDocumentType.CreditNote;
      default:
        return ApDocumentType.Invoice;
    }
  }

  // ---- Debit/Credit Note source invoice (#4473/#4477) ----

  /** Searches the company's posted Invoices and Receipts by number to adjust with this note (#6324): Receipts
   *  too, since the AP initial load registered opening balances as Receipts; and paid ones too — a credit on a
   *  paid invoice becomes the provider's open credit, a charge on one is its own payable. */
  public fetchSourceInvoices = (query: string): Observable<ApDocument[]> =>
    this.service
      .search(
        { number: query, fiscalNumber: query, documentTypes: [ApDocumentType.Invoice, ApDocumentType.Receipt] },
        1,
        10,
        undefined,
        -1,
        true,
      )
      .pipe(
        map((response) =>
          (response.data?.items ?? []).filter(
            (d) =>
              d.status === ApDocumentStatus.Posted || d.status === ApDocumentStatus.PartiallyPaid || d.status === ApDocumentStatus.Paid,
          ),
        ),
      );

  /** #6324 — the Motivo. Moving to or from Financiera changes where the lines come from, so it starts them
   *  over; between the other three the seeded lines stay and only what was typed into them is cleared. */
  onNoteReasonChange(reason: ApNoteReason | null): void {
    const wasFinancial = this.isFinancialNote();
    this.noteReason.set(reason);
    this.form.controls.noteCause.setValue(null, { emitEvent: false });
    this.loadNoteCauses();

    const provider = this.form.controls.provider;
    if (this.isFinancialNote()) {
      provider.setValidators([Validators.required]);
      if (!wasFinancial) {
        this.onSourceInvoiceCleared();
        this.addLine();
      }
    } else {
      provider.clearValidators();
      if (wasFinancial) {
        this.selectedProvider.set(null);
        provider.setValue(null, { emitEvent: false });
        this.lines.clear();
      }
      for (const line of this.lines.controls) this.clearNoteInputs(line);
    }
    provider.updateValueAndValidity({ emitEvent: false });
    this.refreshAfterLineChange();
  }

  /** The causes the API accepts for this note's type and reason. */
  private loadNoteCauses(): void {
    const reason = this.noteReason();
    if (!this.isNote() || reason === null) {
      this.noteCauseOptions.set([]);
      return;
    }
    this.service.getNoteCauses(this.documentTypeSig(), reason).subscribe({
      next: (response) =>
        this.noteCauseOptions.set(
          (response.data ?? []).map((cause) => ({ label: this.noteCauseLabels[cause] ?? String(cause), value: cause })),
        ),
      error: () => this.noteCauseOptions.set([]),
    });
  }

  private clearNoteInputs(line: FormGroup<ApDocumentLineFormGroup>): void {
    line.patchValue(
      {
        noteQuantity: null,
        notePriceMode: null,
        noteCorrectUnitPrice: null,
        noteAmount: null,
        noteAdjustsTax: false,
        noteCorrectTaxRate: null,
      },
      { emitEvent: false },
    );
  }

  /** #7052 — the reason fields of a note line, which open only once the line is checked. */
  private noteInputControls(line: FormGroup<ApDocumentLineFormGroup>): AbstractControl[] {
    const c = line.controls;
    return [c.noteQuantity, c.notePriceMode, c.noteCorrectUnitPrice, c.noteAmount, c.noteAdjustsTax, c.noteCorrectTaxRate];
  }

  private setNoteInputsEnabled(line: FormGroup<ApDocumentLineFormGroup>, enabled: boolean): void {
    for (const control of this.noteInputControls(line)) {
      if (enabled) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    }
  }

  /** #7052 — whether a note line is part of the note: checked by the clerk, or any line of a Financiera note. */
  isNoteLineSelected(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    return this.isFinancialNote() || line.controls.noteSelected.value;
  }

  /** #7052 — checking a line opens its reason fields; unchecking it clears and closes them again. */
  onNoteLineToggled(line: FormGroup<ApDocumentLineFormGroup>): void {
    const selected = line.controls.noteSelected.value;
    if (!selected) this.clearNoteInputs(line);
    this.setNoteInputsEnabled(line, selected);
    this.recomputeTotals();
  }

  /** The adjustment a note line carries, as the API takes it. */
  private noteAdjustmentOf(line: FormGroup<ApDocumentLineFormGroup>): ApNoteAdjustment {
    const lv = line.getRawValue();
    return {
      quantity: lv.noteQuantity,
      priceMode: lv.notePriceMode,
      correctUnitPrice: lv.noteCorrectUnitPrice,
      amount: lv.noteAmount,
      adjustsTax: lv.noteAdjustsTax,
      correctTaxRate: lv.noteCorrectTaxRate,
      sourceQuantity: lv.sourceQuantity,
      sourceUnitPrice: lv.sourceUnitPrice,
      sourceLineTotal: lv.sourceLineTotal,
      sourceTaxRate: lv.sourceTaxRate,
      sourceTaxAmount: lv.sourceTaxAmount,
    };
  }

  /** What a note line will post as, previewed with the API's own rules; null while nothing valid is entered. */
  notePreview(line: FormGroup<ApDocumentLineFormGroup>): ApNoteLinePreview | null {
    if (!this.isNoteLineSelected(line)) return null;
    return previewNoteLine(this.documentTypeSig(), this.noteReason(), this.noteAdjustmentOf(line));
  }

  /**
   * #7050 — the note's original Invoice/Receipt in the read-only side panel the Cash Request uses
   * (ApDocumentQuickViewModalComponent): header, lines and totals, without leaving the note. The panel never
   * touches this form, so whatever was typed is still there when it closes.
   */
  openNoteOriginal(): void {
    const originalId = this.noteOriginal()?.id ?? this.noteSourceReference();
    if (_.isNil(originalId)) return;
    this.modalService.open(ApDocumentQuickViewModalComponent, { modalClass: 'modal_middle' }, { documentId: originalId }).subscribe();
  }

  /** #7052 — the original line numbers that are checked but carry nothing the API would accept. */
  private checkedNoteLinesWithoutAdjustment(): number[] {
    return this.lines.controls
      .filter((line) => line.controls.noteSelected.value && this.notePreview(line) === null)
      .map((line) => line.controls.sourceLineNumber.value)
      .filter((lineNumber): lineNumber is number => !_.isNil(lineNumber));
  }

  /** The original line's net unit price — what a corrected price is compared against. */
  sourceNetUnitPrice(line: FormGroup<ApDocumentLineFormGroup>): number {
    const lv = line.getRawValue();
    return lv.sourceQuantity > 0 ? (lv.sourceLineTotal - lv.sourceTaxAmount) / lv.sourceQuantity : 0;
  }

  /** P14 — the original's posted certificates are not adjusted by a note; the clerk is told, never blocked. */
  private loadNoteCertificateWarning(originalId: string | null | undefined): void {
    this.noteOriginalHasPostedCertificate.set(false);
    if (_.isNil(originalId)) return;
    this.withholdingDocumentService.getByDocument(originalId).subscribe({
      next: (response) =>
        this.noteOriginalHasPostedCertificate.set(
          (response.data ?? []).some((certificate) => certificate.status === ApDocumentStatus.Posted),
        ),
      error: () => this.noteOriginalHasPostedCertificate.set(false),
    });
  }

  /** On selecting the source invoice: lock provider + currency and seed one amount-only line per
   * invoice line (description + GL account + source line number; amount defaults to the line total). */
  onSourceInvoiceSelected(invoice: ApDocument): void {
    if (!invoice?.id) return;
    this.selectedSourceInvoice.set(invoice);
    this.noteOriginal.set(invoice);
    this.loadNoteCertificateWarning(invoice.id);
    // The note inherits the invoice's provider (display only — the API resolves it from the reference).
    const pseudoProvider: Business = {
      id: invoice.providerReference ?? undefined,
      name: invoice.providerName ?? invoice.providerCode ?? '',
      code: invoice.providerCode,
      taxIndicatorCode: invoice.providerTaxId ?? undefined,
      businessTypes: [BusinessEntityType.Provider],
      providerDetails: {
        apGlAccountReference: invoice.apAccountReference ?? undefined,
        apGlAccountNumber: invoice.apAccountNumber ?? undefined,
        apGlAccountName: invoice.apAccountName ?? undefined,
        creditDays: invoice.creditDays,
      },
    };
    this.selectedProvider.set(pseudoProvider);
    // #5229 — the invoice carries no provider country, so fetch it; without it the CAI rule cannot tell a
    // Honduran provider from a foreign one.
    this.hydrateProviderCountry(invoice.providerReference);
    // The note is its own fiscal document (#4503): load the provider's CAIs so its own CAI + document number
    // can be captured + validated, exactly like a factura (Honduras Régimen de Facturación).
    if (invoice.providerReference) this.loadProviderCais(invoice.providerReference);
    this.form.controls.currency.setValue(invoice.currency, { emitEvent: false });
    this.form.controls.currency.disable({ emitEvent: false });
    this.selectedCurrency.set(invoice.currency);
    this.loadRateInForce(invoice.currency);

    // #6324 — a debit note starts from the original's term (overridable); a credit note has none.
    this.previewPaymentTerm(invoice.paymentTermCode ?? null, PaymentTermSource.RelatedDocument, null);

    this.lines.clear();
    for (const line of invoice.lineItems ?? []) this.lines.push(this.buildNoteLine(line));
    this.refreshAfterLineChange();
    this.loadNoteLineAccountNames();
  }

  /**
   * #6324 — the account names for the note lines copied from the original. A line carries only the account's
   * id and number, so the names come from the company's chart of accounts — one cached request, never one per
   * line. The table shows the usual account field once a line has its name.
   */
  private loadNoteLineAccountNames(): void {
    this.siteCompany
      .getFinancialCompanyId(SiteService.getSelectedSiteId())
      .pipe(switchMap((companyId) => (companyId ? this.financialAccountService.getByCompany(companyId) : of(null))))
      .subscribe({
        next: (response) => {
          const names = new Map(
            (response?.data ?? []).map((account) => [account.id, this.localeHelper.getLocalizedValue(account.accountName)]),
          );
          for (const line of this.lines.controls) {
            const reference = line.controls.glAccountReference.value;
            if (!_.isNil(reference) && !line.controls.glAccountName.value)
              line.controls.glAccountName.setValue(names.get(reference) || null, { emitEvent: false });
          }
        },
        error: () => undefined,
      });
  }

  onSourceInvoiceCleared(): void {
    this.selectedSourceInvoice.set(null);
    this.noteOriginal.set(null);
    this.noteOriginalHasPostedCertificate.set(false);
    this.selectedProvider.set(null);
    this.form.controls.currency.enable({ emitEvent: false });
    this.lines.clear();
    this.refreshAfterLineChange();
  }

  private enumToOptions(enumName: string): EnumOption[] {
    const values = this.enumService.getEnum(enumName).values;
    return Object.entries(values).map(([key, label]) => ({ label: label as string, value: Number(key) }));
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  /** This document's status by default; a note's Original document panel passes the original's (#6324). */
  statusLabelText(status: ApDocumentStatus = this.status()): string {
    return this.statusLabels[status] ?? '';
  }

  statusSeverity(status: ApDocumentStatus = this.status()): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case ApDocumentStatus.Paid:
        return 'success';
      case ApDocumentStatus.Posted:
        return 'info';
      case ApDocumentStatus.PartiallyPaid:
        return 'warn';
      case ApDocumentStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  private get companyReference(): string {
    return SiteService.getSelectedSiteId() ?? '';
  }

  // ---- lines ----
  private buildLineGroup(item?: ItemMasterDto, poItem?: ItemInPurchaseOrderDto, po?: PurchaseOrderDto): FormGroup<ApDocumentLineFormGroup> {
    // PO-sourced lines lock the unit price to the PO; quantity stays editable.
    const priceLocked = !!po;
    // #4723 — a taxable PO item (taxRate > 0) folds its tax into the AP unit price so the AP total already
    // reflects the tax; the API extracts it on save and posts it to recoverable VAT: unit = tax-inclusive TotalPrice ÷ qty
    // (e.g. 100 + 15% → 115 / 10 = 11.50). Exempt / free lines keep the plain PO unit price. Rounded to
    // 4 dp so an uneven split doesn't drop cents.
    const taxRate = poItem?.taxRate ?? 0;
    const orderedQty = poItem?.quantity ?? 0;
    const unitPrice =
      taxRate > 0 && poItem?.totalPrice != null && orderedQty > 0
        ? Math.round((poItem.totalPrice / orderedQty) * 10000) / 10000
        : (poItem?.unitPrice ?? 0);
    return this.formBuilder.group<ApDocumentLineFormGroup>({
      itemSelection: new FormControl<ItemMasterDto | null>(item ?? null),
      description: new FormControl<string | null>(item ? this.itemLongName(item) : null, [Validators.required]),
      glAccountReference: new FormControl<string | null>(null, [Validators.required]),
      glAccountNumber: new FormControl<string | null>(null),
      glAccountName: new FormControl<string | null>(null),
      uom: new FormControl<number | null>(poItem?.uom ?? item?.unitOfMeasure ?? null),
      quantity: new FormControl<number>(poItem?.quantity ?? 1, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0.0001)],
      }),
      unitPrice: new FormControl<number>(
        { value: unitPrice, disabled: priceLocked },
        { nonNullable: true, validators: [Validators.required, Validators.min(0)] },
      ),
      taxRate: new FormControl<number>(taxRate, { nonNullable: true }),
      purchaseOrderItemId: new FormControl<string | null>(poItem?.id ?? null),
      purchaseOrderReference: new FormControl<string | null>(po?.id ?? null),
      purchaseOrderNumber: new FormControl<string | null>(po?.purchaseOrderNumber ?? null),
      sourceLineNumber: new FormControl<number | null>(null),
      // #4842 — an item line takes the master's classification and is disabled (the API re-asserts it from
      // the master anyway, so an editable control here would only ever be a lie); a description-only line has
      // nothing to classify, so the clerk must pick one.
      itemClassificationCode: new FormControl<string | null>({ value: item?.itemClassificationCode ?? null, disabled: !!item }, [
        Validators.required,
      ]),
      noteSelected: new FormControl<boolean>(false, { nonNullable: true }),
      noteQuantity: new FormControl<number | null>(null),
      notePriceMode: new FormControl<ApNotePriceMode | null>(null),
      noteCorrectUnitPrice: new FormControl<number | null>(null),
      noteAmount: new FormControl<number | null>(null),
      noteAdjustsTax: new FormControl<boolean>(false, { nonNullable: true }),
      noteCorrectTaxRate: new FormControl<number | null>(null),
      sourceQuantity: new FormControl<number>(0, { nonNullable: true }),
      sourceUnitPrice: new FormControl<number>(0, { nonNullable: true }),
      sourceLineTotal: new FormControl<number>(0, { nonNullable: true }),
      sourceTaxRate: new FormControl<number>(0, { nonNullable: true }),
      sourceTaxAmount: new FormControl<number>(0, { nonNullable: true }),
    });
  }

  /** #4842 — keeps the classification control in step with the line's item: picking an item locks the control
   * to that item's classification, clearing it hands the choice back to the clerk. Called from the item
   * type-ahead so a line that changes item never keeps the previous item's classification. */
  private syncLineClassification(group: FormGroup<ApDocumentLineFormGroup>, item: ItemMasterDto | null): void {
    const control = group.controls.itemClassificationCode;
    if (item) {
      control.setValue(item.itemClassificationCode ?? null, { emitEvent: false });
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  /** Seeds one note line from an original line (#4473/#4477, #6324): the line it adjusts, its account
   * (locked — the API takes it from the original anyway) and a copy of its quantity, price and ISV for the
   * reason's columns. Nothing is entered: a note may adjust only some of the original's lines, so each starts
   * unchecked and only the ones the user checks (#7052) are sent. */
  private buildNoteLine(source: ApDocumentLine): FormGroup<ApDocumentLineFormGroup> {
    const group = this.buildLineGroup();
    group.patchValue(
      {
        description: source.description ?? null,
        glAccountReference: source.glAccount?.reference ?? null,
        glAccountNumber: source.glAccount?.number ?? null,
        uom: source.unitOfMeasure ?? null,
        unitPrice: 0,
        taxRate: source.taxRate ?? 0,
        sourceLineNumber: source.lineNumber,
        // #7051 — the original line's PO, so the same item on two POs reads as two lines. Display and grouping
        // only: a note is not an invoice of the PO, so the payload leaves it out (the API clears it anyway).
        ...this.notePurchaseOrderOf(source),
        sourceQuantity: source.quantity ?? 0,
        sourceUnitPrice: source.unitPrice ?? 0,
        sourceLineTotal: source.lineTotal ?? 0,
        sourceTaxRate: source.taxRate ?? 0,
        sourceTaxAmount: source.taxAmount ?? 0,
        // #4842 — a note line adjusts a specific invoice line, so it inherits that line's classification
        // rather than asking the user to re-classify it. Locked for the same reason the amount is the only
        // editable field here.
        itemClassificationCode: source.itemClassificationCode ?? null,
      },
      { emitEvent: false },
    );
    group.controls.itemClassificationCode.disable({ emitEvent: false });
    // #7052 — unchecked until the clerk picks the line: blank and not editable.
    this.setNoteInputsEnabled(group, false);
    // #6324 — units past what the original line invoiced are an error the clerk sees, not a number the input
    // quietly rewrites. A credit note returns at most what was invoiced, and a price correction re-prices at
    // most that; a Cantidad debit note may charge MORE (its cap is units received, which only the API knows).
    group.controls.noteQuantity.addValidators((control) => {
      const units = control.value;
      const priceCorrection =
        this.noteReason() === ApNoteReason.PriceOrValue && group.controls.notePriceMode.value === ApNotePriceMode.CorrectUnitPrice;
      const capped = priceCorrection || (this.noteReason() === ApNoteReason.Quantity && this.isCreditNote());
      return capped && units != null && units > (group.controls.sourceQuantity.value ?? 0) ? { exceedsSource: true } : null;
    });
    // Switching to a fixed amount hides the units, so their error must not outlive the field.
    group.controls.notePriceMode.valueChanges.subscribe(() => group.controls.noteQuantity.updateValueAndValidity({ emitEvent: false }));
    return group;
  }

  /** #7051 — the PO an original line was invoiced on; nulls for a line with none (a charge, an initial-load Receipt). */
  private notePurchaseOrderOf(source: ApDocumentLine): { purchaseOrderReference: string | null; purchaseOrderNumber: string | null } {
    return {
      purchaseOrderReference: source.purchaseOrderKey?.reference ?? null,
      purchaseOrderNumber: source.purchaseOrderKey?.number ?? null,
    };
  }

  /** #7051 — a note's PO group shows its PO; the no-PO group gets a header only when PO groups sit beside it. */
  showNotePoGroupHeader(group: PoGroup): boolean {
    return !this.isFinancialNote() && (group.reference !== '' || this.poGroups().length > 1);
  }

  /** #7051 — a saved note's lines come back without a PO (the API keeps none on a note), so it is read from the
   *  original line each one adjusts. */
  private applyNoteLinePurchaseOrders(original: ApDocument): void {
    const sourceLines = new Map((original.lineItems ?? []).map((line) => [line.lineNumber, line]));
    for (const line of this.lines.controls) {
      const sourceLineNumber = line.controls.sourceLineNumber.value;
      const source = _.isNil(sourceLineNumber) ? undefined : sourceLines.get(sourceLineNumber);
      if (source) line.patchValue(this.notePurchaseOrderOf(source), { emitEvent: false });
    }
  }

  /** Localized long display name for an item — used as the line description text and in the search list. */
  itemLongName(item: ItemMasterDto): string {
    return this.localeHelper.getLocalizedValue(item.itemLongDisplayName) || item.itemCode || '';
  }

  /** UnitOfMeasure label for the qty addon (empty for free-description lines). */
  getUnitName(uom: number | null): string {
    if (uom === null || uom === undefined) return '';
    return this.uoms[uom] ?? '';
  }

  /** Minimal ItemMasterDto reconstructed from a saved line (only itemCode is needed for display). */
  private pseudoItem(itemCode: string, longName: string): ItemMasterDto {
    const translation = { es: longName, en: longName };
    return { itemCode, itemDisplayName: translation, itemLongDisplayName: translation };
  }

  getStrictItem(item: unknown): ItemMasterDto {
    return item as ItemMasterDto;
  }

  /** Unwraps a grouped-row ({ index, group }) to its line FormGroup for the template. */
  getLineGroup(row: unknown): FormGroup<ApDocumentLineFormGroup> {
    return (row as { group: FormGroup<ApDocumentLineFormGroup> }).group;
  }

  getLineIndex(row: unknown): number {
    return (row as { index: number }).index;
  }

  /** Free/empty line — used by the "Add Line" button for non-item document types. */
  addLine(): void {
    this.lines.push(this.buildLineGroup());
    // The table renders from poGroups() — rebuild it so the new line actually shows.
    this.refreshAfterLineChange();
  }

  /** #4725 — adds a general charge/fee line (description + amount, qty 1, own GL account) with no PO
   * reference. It adds to the document total and is payable to the provider — the backend treats it as
   * any free line (no PurchaseOrderKey). Shown in the Charges / Fees section on PO-driven Invoices. */
  addChargeLine(): void {
    this.lines.push(this.buildLineGroup());
    this.refreshAfterLineChange();
  }

  /** Item flow: selecting an item in the search adds a line and clears the search box. */
  addLineFromItem(item: ItemMasterDto): void {
    if (!item) return;
    this.lines.push(this.buildLineGroup(item));
    this.lineItemSearch.reset(null, { emitEvent: false });
    // The table renders from poGroups() — rebuild it so the new line actually shows.
    this.refreshAfterLineChange();
  }

  removeLine(index: number): void {
    this.lines.removeAt(index);
    this.refreshAfterLineChange();
  }

  // ── attachments ──────────────────────────────────────────────────────────

  /**
   * Uploads the chosen file and replaces the list with what the server came back with.
   *
   * <p>The input is cleared either way. A file input keeps its value after a change, so picking the SAME
   * file again would raise no event and the upload would silently appear to do nothing — the most confusing
   * possible outcome for a user who is re-trying after a failure.</p>
   */
  onAttachmentSelected(event: Event, input: HTMLInputElement): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    const id = this.currentId();
    if (!file || !id) {
      input.value = '';
      return;
    }

    this.startLoading();
    this.service
      .uploadAttachment(id, file)
      .pipe(
        finalize(() => {
          this.stopLoading();
          input.value = '';
        }),
      )
      .subscribe({
        next: (response) => {
          this.attachments.set(response.data?.attachments ?? []);
          GlobalMessageUtil.success(
            this.t('ApDocument_Attachment_Added_Title', 'File attached'),
            this.t('ApDocument_Attachment_Added', 'The file was attached to this document.'),
          );
        },
        error: () => undefined,
      });
  }

  /** Removing a file deletes it — there is no undo — so it is confirmed first, naming the file. */
  removeAttachment(attachment: S3Object): void {
    const id = this.currentId();
    if (!id) return;

    this.confirmationService.confirm({
      header: this.t('ApDocument_Attachment_Remove_Title', 'Remove attachment'),
      message: this.t('ApDocument_Attachment_Remove_Confirm', 'Remove "{file}" from this document? The file is deleted.').replace(
        '{file}',
        this.attachmentFileName(attachment),
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Remove', 'Remove'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.startLoading();
        this.service
          .removeAttachment(id, attachment.name)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (response) => {
              this.attachments.set(response.data?.attachments ?? []);
              GlobalMessageUtil.success(
                this.t('ApDocument_Attachment_Removed_Title', 'File removed'),
                this.t('ApDocument_Attachment_Removed', 'The file was removed from this document.'),
              );
            },
            error: () => undefined,
          });
      },
    });
  }

  /** The file name a person recognises, out of the full S3 key (Finance/ApDocument/{id}/invoice.pdf). */
  attachmentFileName(attachment: S3Object): string {
    const name = attachment?.name ?? '';
    return name.substring(name.lastIndexOf('/') + 1) || name;
  }

  /** Opens the presigned URL in a new tab. `noopener` because the URL is a credential of sorts — the tab it
   *  opens has no business holding a reference back to this window. */
  openAttachment(attachment: S3Object): void {
    if (attachment?.presignedUrl) window.open(attachment.presignedUrl, '_blank', 'noopener');
  }

  // ── received date (#5165) ────────────────────────────────────────────────

  /**
   * Applies the rules that depend on the document KIND, which the clerk can change while the form is open:
   * required on an Invoice, and read-only when the date belongs to a shipment rather than to them.
   *
   * Called whenever the kind or the pre-invoice source changes, not once at init — a form that starts as a
   * Receipt and becomes an Invoice has to gain the requirement, and the reverse has to lose it rather than
   * leaving an untouchable invalid control behind.
   */
  private applyReceivedDateRules(): void {
    const control = this.form.controls.receivedDate;
    const required = this.receivedDateRequired();

    control.setValidators(required ? [Validators.required, this.notBeforeDocumentDate()] : [this.notBeforeDocumentDate()]);

    // The shipment owns the date on an Order-Invoice-sourced document; the API overwrites whatever is sent,
    // so offering an editable control would be a lie about who decides it.
    if (this.receivedDateMode() === 'shipment') control.disable({ emitEvent: false });
    else if (this.isDraft()) control.enable({ emitEvent: false });

    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * The received date cannot precede the document date — the provider issues at dispatch and we receive on
   * or after that day. Mirrors the API rule, including comparing by DAY: an invoice dated at midnight and a
   * receipt keyed at 14:30 the same day are the same day, and a raw comparison would reject it.
   */
  private notBeforeDocumentDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const received = control.value as Date | null;
      const documentDate = this.form?.controls.documentDate.value;
      if (!received || !documentDate) return null;

      const receivedDay = new Date(received.getFullYear(), received.getMonth(), received.getDate());
      const documentDay = new Date(documentDate.getFullYear(), documentDate.getMonth(), documentDate.getDate());
      return receivedDay < documentDay ? { receivedBeforeDocument: true } : null;
    };
  }

  /**
   * Loads the days something was received against the POs currently on the document (#5165).
   *
   * One call for every PO rather than one per PO, and re-run whenever the PO set changes, since adding a PO
   * can introduce a receiving date the clerk should be able to pick. An empty answer is normal — nothing
   * receipted yet — and drops the field to a free picker rather than blocking registration.
   */
  private loadReceivedDateOptions(): void {
    // A pre-invoice is dated by its SHIPMENT (loadShipmentReceivedDate), so its POs are the wrong place to
    // ask — an imported shipment's POs commonly have no Goods Receipt Notes at all, and the empty answer
    // would read as "nothing received yet" for goods that are already in the warehouse.
    if (this.documentTypeSig() !== ApDocumentType.Invoice || this.receivedDateMode() === 'shipment') {
      this.receivedDateOptions.set([]);
      this.receivedDateOptionsLoaded.set(false);
      return;
    }

    const poIds = Array.from(
      new Set(
        this.lines.controls
          .map((line) => line.controls.purchaseOrderReference.value)
          .filter((reference): reference is string => !!reference),
      ),
    ).sort();

    // Editing a description or a quantity does not change which POs are on the document, and this runs on
    // every line edit — so re-ask only when the PO set itself moved.
    const signature = poIds.join('|');
    if (signature === this.loadedReceivedDatePoSignature()) return;
    this.loadedReceivedDatePoSignature.set(signature);

    if (poIds.length === 0) {
      this.receivedDateOptions.set([]);
      this.receivedDateOptionsLoaded.set(true);
      return;
    }

    this.service.getReceivedDateOptions(poIds).subscribe({
      next: (response) => {
        this.receivedDateOptions.set((response.data ?? []).map((iso) => new Date(iso)));
        this.receivedDateOptionsLoaded.set(true);
        this.applyReceivedDateRules();
      },
      error: () => {
        // A failed lookup must not block registration: fall back to letting the clerk key the date.
        this.receivedDateOptions.set([]);
        this.receivedDateOptionsLoaded.set(true);
      },
    });
  }

  /**
   * Fills the received date from the shipment carrying this Order Invoice (#5165).
   *
   * The field is read-only in this mode because the shipment owns the date — but read-only is not the same
   * as unknowable, and leaving it blank told the officer "not received yet" for goods that had been in the
   * warehouse for weeks. The API applies the identical rule on save, so this is a preview of the stored
   * value rather than a second opinion about it.
   *
   * A null answer is normal and is left as null: the shipment really has not been received, and the
   * "pending shipment" hint is then telling the truth.
   */
  private loadShipmentReceivedDate(orderInvoiceReference: string | null): void {
    if (!orderInvoiceReference) return;

    this.service.getShipmentReceivedDate(orderInvoiceReference).subscribe({
      next: (response) => {
        const received = response.data ? new Date(response.data) : null;
        if (!received || isNaN(received.getTime())) return;
        this.form.controls.receivedDate.setValue(received, { emitEvent: false });
        this.applyReceivedDateRules();
      },
      // A failed lookup must not block registration — the API resolves the date on save regardless.
      error: () => undefined,
    });
  }

  // ---- withholding requirement (#4842/#5166) ----

  /** Asserts a certificate IS needed, overriding a classification rule that said otherwise (#5166). */
  openRequireWithholdingDialog(): void {
    this.withholdingIntent.set(true);
    this.withholdingReason.reset('');
    this.withholdingDialogVisible.set(true);
  }

  /** Asserts a certificate is NOT needed, dropping the payment gate (#4842). */
  openWaiveDialog(): void {
    this.withholdingIntent.set(false);
    this.withholdingReason.reset('');
    this.withholdingDialogVisible.set(true);
  }

  confirmWithholdingChange(): void {
    const id = this.currentId();
    if (!id) return;
    if (this.withholdingReason.invalid) {
      this.withholdingReason.markAsTouched();
      return;
    }

    const requiring = this.withholdingIntent();
    this.withholdingSaving.set(true);
    this.service
      .changeWithholdingRequirement(id, requiring, this.withholdingReason.value.trim())
      .pipe(finalize(() => this.withholdingSaving.set(false)))
      .subscribe({
        next: (response) => {
          this.applyWithholdingResponse(response.data);
          this.withholdingDialogVisible.set(false);
          GlobalMessageUtil.success(
            requiring
              ? this.t('ApDocument_Withholding_Required_Title', 'Withholding required')
              : this.t('ApDocument_WithholdingWaived_Title', 'Withholding waived'),
            requiring
              ? this.t(
                  'ApDocument_Withholding_Forced',
                  'This document now requires a withholding certificate, and every line on it can be withheld.',
                )
              : this.t('ApDocument_WithholdingWaived', 'This document no longer requires a withholding certificate.'),
          );
        },
        error: () => undefined,
      });
  }

  /**
   * How many certificates already cite this document (#4842/#5166). Gates every path that REMOVES a
   * requirement, in both directions — a certificate's lines exist because the document required them.
   */
  private loadCitingCertificateCount(id: string): void {
    this.withholdingDocumentService.getByDocument(id).subscribe({
      // Fail CLOSED: if we cannot tell whether a certificate exists, keep the release button disabled rather
      // than offering an action the API will refuse.
      next: (response) => this.citingCertificateCount.set(response.data?.length ?? 1),
      error: () => this.citingCertificateCount.set(1),
    });
  }

  /** Re-reads every withholding field off the saved document, so the panel never drifts from the server. */
  private applyWithholdingResponse(data: ApDocument | null | undefined): void {
    if (!data) return;
    this.requiresWithholding.set(data.requiresWithholding ?? false);
    this.withholdingWaivedReason.set(data.withholdingWaivedReason ?? null);
    this.withholdingWaivedBy.set(data.withholdingWaivedBy ?? null);
    this.withholdingForced.set(data.withholdingForced ?? false);
    this.withholdingForcedReason.set(data.withholdingForcedReason ?? null);
    this.withholdingForcedBy.set(data.withholdingForcedBy ?? null);
  }

  lineTotal(line: FormGroup<ApDocumentLineFormGroup>): number {
    // A note line's total is derived from its reason (#6324), not typed.
    if (this.isNote()) return this.notePreview(line)?.lineTotal ?? 0;
    return (line.controls.quantity.value ?? 0) * (line.controls.unitPrice.value ?? 0);
  }

  /** #4723 — true when the line's unit price folds in the PO item's tax (taxRate > 0). The unit price
   * shown is tax-inclusive (TotalPrice ÷ qty) and the line still posts as Exempt, so an info note is
   * displayed next to the price to explain why it differs from the plain PO unit price. */
  lineIncludesTax(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    return (line.controls.taxRate.value ?? 0) > 0;
  }

  /** The line's tax rate as a whole-number percent (0.15 → 15) for the tax-inclusive note. */
  lineTaxRatePercent(line: FormGroup<ApDocumentLineFormGroup>): number {
    return Math.round((line.controls.taxRate.value ?? 0) * 10000) / 100;
  }

  /** Total and unit price are two views of the same line, tied together by the quantity (total = qty ×
   * unit price). Editing the total back-solves the unit price (total ÷ qty) so the three stay consistent;
   * qty ≤ 0 falls back to treating the total as the unit price. Rounded to 4 dp so a total like 100 over a
   * qty of 3 divides cleanly instead of losing cents. */
  onLineTotalChange(line: FormGroup<ApDocumentLineFormGroup>, total: number | null): void {
    const qty = line.controls.quantity.value ?? 0;
    const amount = total ?? 0;
    const unit = qty > 0 ? amount / qty : amount;
    line.controls.unitPrice.setValue(Math.round(unit * 10000) / 10000, { emitEvent: false });
    this.recomputeTotals();
  }

  /** The item's configured GL account numbers (#4306) — restricts a line's GL picker to the item's ledgers.
   * Applies to any line with a selected item (Invoice PO lines + searched items on a Receipt / Purchase
   * Receipt): the account can't be free-for-all when the item master already scopes it. Null when the line
   * has no item or the item has no accounts → full posting-account selection. */
  /**
   * Whether a line is a SERVICE line, by the API's own rule (#4842) — the same one the withholding preview
   * reads, so the screen cannot hold two different opinions about what a service is.
   *
   * <p><b>Unknown must not mean "goods".</b> The rules arrive asynchronously and a line may be classified
   * before they land. Guessing "goods" would auto-assign a GL account and LOCK it, which is the answer that
   * cannot be undone from the screen; reporting "service" leaves the clerk a free picker and nothing
   * assigned. So an unloaded rule set, and an unclassified line, both read as service.</p>
   */
  private lineIsService(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    const rules = this.withholdingRules();
    if (!rules || rules.treatAllAsService) return true;
    const code = line.getRawValue().itemClassificationCode;
    if (!code?.trim()) return true;
    return new Set(rules.serviceClassificationCodes.map((c) => c.toUpperCase())).has(code.trim().toUpperCase());
  }

  /**
   * The item's GL account when it has exactly ONE — what a goods (Bien) line posts to.
   *
   * <p>Exactly one, not "the first of several": a goods item is configured with a single account, and
   * silently taking the first of two would assign an account nobody chose and then lock it. An item with
   * several falls back to the ordinary picker, restricted to its own list.</p>
   */
  private singleItemGlAccount(line: FormGroup<ApDocumentLineFormGroup>): { reference: string; number: string | null } | null {
    const accounts = (line.controls.itemSelection.value?.glAccounts ?? []).filter((account) => !!account?.reference);
    if (accounts.length !== 1) return null;
    return { reference: accounts[0].reference!, number: accounts[0].number ?? null };
  }

  /**
   * A goods line whose account comes from the item master, not from the clerk (#4306). The picker renders
   * read-only: there is one valid account, so an editable field could only ever be used to get it wrong,
   * and the item master is where it changes.
   */
  lineGlAccountLocked(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    // An unclassified item line has no business offering a free picker. lineIsService reports "service" for
    // it (deliberately — see that method), which would otherwise open the WHOLE chart of accounts on a line
    // that cannot be saved anyway, and whose account the item master will decide once Data Master classifies
    // it. Locking it keeps the clerk from making a choice that is about to be overruled.
    if (this.lineMissingMasterClassification(line)) return true;
    return !this.lineIsService(line) && !!this.singleItemGlAccount(line);
  }

  /**
   * An ITEM line whose item master carries no classification (#4842).
   *
   * <p>The classification of an item line belongs to the item master, not to this screen: the control is
   * disabled, and <c>StampLineClassificationAsync</c> re-asserts the master's value on every save. When the
   * master has none there is nothing to assert and the line saves blank — which is not neutral.
   * <c>WithholdingClassificationHelper</c> reads an unclassified line as a SERVICE, so the document
   * silently demands a Constancia de Retención that may not be owed, and the clerk is never told why.</p>
   *
   * <p><b>Why a message and not a picker.</b> Letting the clerk choose here would not survive: the API
   * overwrites the line from the master the moment that master gains a classification, so the choice is
   * discarded without warning. The only durable fix is on the item itself, which is Data Master's to make.
   * A description-only line is the opposite case — it has no item to classify, so its picker is correct and
   * this returns false for it.</p>
   */
  lineMissingMasterClassification(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    // An ENABLED control is the clerk's to fill, so it is not this message's case and not this guard's
    // either: Validators.required runs on it and reportInvalidFormFields names the field. A receipt-sourced
    // line sits here briefly — buildPreReceiptLines leaves an unclassified line open — until resolveLineItems
    // swaps in the real item master and syncLineClassification locks it. Only then is Data Master the answer.
    if (line.controls.itemClassificationCode.enabled) return false;
    const raw = line.getRawValue();
    // A note line carries no item (buildNoteLine inherits the source invoice line's value and locks it), so
    // there is no item master to fix and nothing to ask for.
    if (!raw.itemSelection?.itemCode) return false;
    return !raw.itemClassificationCode?.trim();
  }

  /** The item codes blocking a save for want of a classification — de-duplicated, in line order. */
  private unclassifiedItemCodes(): string[] {
    const codes = this.lines.controls
      .filter((line) => this.lineMissingMasterClassification(line))
      .map((line) => line.getRawValue().itemSelection?.itemCode ?? '')
      .filter((code): code is string => !!code);
    return [...new Set(codes)];
  }

  lineGlAccountNumbers(line: FormGroup<ApDocumentLineFormGroup>): string[] | null {
    const item = line.controls.itemSelection.value;
    // A service line picks freely from the chart of accounts — the item master's list is a starting point
    // for goods, but a service can be booked against any expense account the clerk judges right.
    if (this.lineIsService(line)) return null;
    // Free (item-less) line — no allow-list, the whole chart of accounts is fair game.
    if (!item) return null;
    const numbers = (item.glAccounts ?? []).map((g) => g.number).filter((n): n is string => !!n);
    // The account the line ALREADY carries stays in the list even once the item master has dropped it,
    // so the picker shows the document's own assignment instead of pruning it out of its own tree.
    const assigned = line.controls.glAccountNumber.value;
    if (assigned && !numbers.includes(assigned)) numbers.push(assigned);
    return numbers.length ? numbers : null;
  }

  /**
   * True when a line's item has NO GL accounts configured in the item master (#4306) — the GL picker is
   * hidden and an info hint is shown instead (the accounts must be set on the item master). Free (item-less)
   * lines keep the normal picker.
   *
   * A line that already carries an account is NOT missing one: that account belongs to the DOCUMENT, chosen
   * when the line was written, and the item master dropping it since neither un-assigns it nor makes it
   * wrong — the document keeps posting to it. Showing the hint there would hide an assigned account behind
   * "no GL accounts on item" and leave it unreadable and unchangeable. Clear the account and the hint comes
   * back, correctly this time: there is then nothing on the item to pick from.
   */
  lineItemMissingGl(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    const item = line.controls.itemSelection.value;
    // A service line does not draw on the item's accounts at all, so it can never be missing them — it
    // gets the ordinary picker over the whole chart of accounts.
    if (this.lineIsService(line)) return false;
    if (!item || line.controls.glAccountReference.value) return false;
    return !item.glAccounts?.length;
  }

  private recomputeTotals(): void {
    let gross = 0;
    let feesNet = 0;
    let tax = 0;
    for (const line of this.lines.controls) {
      const lineGross = this.lineTotal(line);
      gross += lineGross;

      // #4723 — a taxable PO item's unit price is tax-INCLUSIVE (the PO's tax-inclusive TotalPrice ÷ qty),
      // so the tax is already inside the line total rather than added on top. Extract it rather than
      // reading ApDocument.TaxAmount, which the API forces to 0 because the line posts as exempt:
      //   gross = net × (1 + r)  ⇒  tax = gross × r / (1 + r)
      // Identical to the API's ComputeLineTotals: round the NET, derive the tax by subtraction. Summing
      // an unrounded per-line tax and rounding once at the end drifts a cent from what actually posts.
      const rate = line.controls.taxRate.value ?? 0;
      const lineTax = this.isNote()
        ? (this.notePreview(line)?.taxAmount ?? 0)
        : rate > 0
          ? lineGross - Math.round((lineGross / (1 + rate)) * 100) / 100
          : 0;
      tax += lineTax;

      // #4725 charge / fee lines are the PO-driven Invoice's lines with no PO behind them. They are part of
      // the document total already — the summary breaks them out, it does not add them on top.
      if (this.useItemTypeAhead() && !line.controls.purchaseOrderReference.value) feesNet += lineGross - lineTax;
    }
    const round = (v: number) => Math.round(v * 100) / 100;
    this.subtotal.set(round(gross));
    this.feesTotal.set(round(feesNet));
    this.embeddedTax.set(round(tax));
    if (this.isNote()) this.recomputeNoteFlags();
  }

  /** #6324 — whether the note has content yet (locks the Motivo), and the P13 month warning: a credit note on
   *  service lines dated in another month than its original, which Sierra's policy says should be re-invoiced. */
  private recomputeNoteFlags(): void {
    const filled = this.lines.controls.filter((line) => this.notePreview(line) !== null);
    // #7052 — a checked line is already a choice made under this Motivo, even before anything is typed in it.
    this.noteHasContent.set(this.lines.controls.some((line) => this.isNoteLineSelected(line)));

    const original = this.noteOriginal();
    const noteDate = this.form.controls.documentDate.value;
    const originalDate = original?.documentDate ? new Date(original.documentDate) : null;
    this.noteServiceMonthMismatch.set(
      this.isCreditNote() &&
        !!noteDate &&
        !!originalDate &&
        (noteDate.getFullYear() !== originalDate.getFullYear() || noteDate.getMonth() !== originalDate.getMonth()) &&
        filled.some((line) => this.lineIsService(line)),
    );
  }

  // ---- provider ----
  public fetchProviders = (query: string): Observable<Business[]> =>
    this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));

  /**
   * The provider's AP control account is required to register a document — it posts the credit side of the
   * invoice's journal entry. Shows an error and returns false when missing. Reused by the provider picker
   * and the pre-invoice flow (the server re-checks on create).
   */
  private ensureProviderHasApAccount(provider: Business | null): boolean {
    const details = provider?.providerDetails;
    if (!details?.apGlAccountReference || !details?.apGlAccountNumber) {
      GlobalMessageUtil.error(
        this.t('ApDocument_Provider_NoApAccount_Title', 'AP account required'),
        this.t(
          'ApDocument_Provider_NoApAccount',
          'This provider has no AP control account set. Add the account reference and number in the provider details before creating a document for them.',
        ),
      );
      return false;
    }
    return true;
  }

  onProviderSelected(provider: Business): void {
    if (!this.ensureProviderHasApAccount(provider)) {
      this.form.controls.provider.setValue(null, { emitEvent: false });
      this.onProviderCleared();
      return;
    }
    this.selectedProvider.set(provider);
    // #4784 — no source document on this path, so the provider's own term is the term.
    this.previewPaymentTerm(null, PaymentTermSource.ProviderDefault, provider.providerDetails?.paymentTermsCode ?? null);
    this.recomputeDueDate();
    // Invoice lines are PO-sourced (tied to this provider), so switching provider must reset them. Free /
    // item-based types (Receipt / Purchase Receipt / Policy) keep the lines the user already entered.
    if (this.useItemTypeAhead()) {
      this.lines.clear();
      this.refreshAfterLineChange();
    } else if (this.lines.length === 0) {
      this.addLine();
    }
    // POs only feed the Invoice item picker — free-description types never draw from POs.
    if (provider?.id && this.useItemTypeAhead()) this.loadProviderPurchaseOrders(provider.id);
    this.syncProviderCais(provider);
  }

  /**
   * Fills the provider's domicile onto a synthetic provider (#5229).
   *
   * <p>`onSourceInvoiceSelected` and `patchFromDocument` rebuild a provider from what the DOCUMENT
   * snapshotted — name, code, tax id, AP account — and a document does not carry the provider's country.
   * `providerCountryHasNoAuthorization` then falls through to `isNew()`, which on a loaded document is
   * false, so a CAI was demanded no matter where the provider was domiciled. Reloading the provider
   * cleared it only because that path fetches the real master record.</p>
   *
   * <p>The requirement itself was never stale — it is a computed read by an effect. Only the country was
   * missing, so this fetches that one field.</p>
   *
   * <p>Copies the country and nothing else: every other field on the pseudo-provider is a deliberate
   * snapshot of what the document was registered with, and must not be replaced by the master's values
   * of today. The one exception is a tax id the document never snapshotted (#6324 — documents registered
   * before the API stamped it): the master's fills the gap, never overwrites. Failure is silent — a
   * document must still open when the provider master cannot be read.</p>
   */
  private hydrateProviderCountry(reference: string | null | undefined): void {
    if (!reference) return;
    this.businessService.getBusinessById(reference).subscribe({
      next: (response) => {
        const countryCode = response.data?.countryCode;
        const masterTaxId = response.data?.taxIndicatorCode;
        const current = this.selectedProvider();
        // Guard against a slow response landing after the clerk moved on to another provider.
        if (!current || current.id !== reference) return;
        const taxIndicatorCode = current.taxIndicatorCode || masterTaxId;
        if (!countryCode && taxIndicatorCode === current.taxIndicatorCode) return;
        this.selectedProvider.set({ ...current, countryCode: countryCode ?? current.countryCode, taxIndicatorCode });
      },
      error: () => undefined,
    });
  }

  /**
   * #4727 — re-fetch the currently selected provider and refresh its AP account / credit days / CAIs
   * (and the Invoice's open POs) WITHOUT clearing the lines. A provider is often picked before its
   * ProviderDetails (AP account, domicile, credit days) are filled in; this pulls the updated master in
   * place. Available on an editable draft even after the provider control is locked.
   */
  reloadProvider(): void {
    const current = this.selectedProvider();
    if (!current?.id) return;
    this.startLoading();
    this.businessService
      .getBusinessById(current.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const provider = response.data;
          if (!provider) return;
          this.selectedProvider.set(provider);
          // Keep the (possibly locked) form control in sync so the tax id / AP account readouts update.
          this.form.controls.provider.setValue(provider, { emitEvent: false });
          this.recomputeDueDate();
          // Refresh the "provider's account today" side of the comparison, so the divergence notice reacts
          // to the reload. The pinned account is deliberately NOT touched — reloading the provider must not
          // re-point a document that is already registered against an account.
          this.providerCurrentApAccountReference.set(provider.providerDetails?.apGlAccountReference ?? null);
          this.providerCurrentApAccountNumber.set(provider.providerDetails?.apGlAccountNumber ?? null);
          // Surface a toast if the AP account is still missing — reload never clears the selection or lines.
          // Only when nothing is pinned yet: the toast is about being able to REGISTER a document, and a
          // saved one already carries its own account and is unaffected by the provider's.
          if (!this.documentApAccountReference()) this.ensureProviderHasApAccount(provider);
          // CAI only applies to Honduras-domiciled providers (#4726) — the reload is exactly when a
          // just-filled-in domicile takes effect, in either direction.
          this.syncProviderCais(provider);
          // Refresh the Invoice's open POs (a PO may have been approved since the provider was picked).
          if (this.useItemTypeAhead()) this.loadProviderPurchaseOrders(provider.id!);
          GlobalMessageUtil.success(
            this.t('ApDocument_Provider_Reloaded_Title', 'Provider refreshed'),
            this.t('ApDocument_Provider_Reloaded', 'The provider details were reloaded.'),
          );
        },
        error: () => undefined,
      });
  }

  /**
   * #4726 / #4791 — the authorization domicile gate, in one place. The dropdown is populated ONLY for a
   * provider whose domicile is a country that issues fiscal authorizations; any other (or unset) domicile
   * drops the options and clears one already picked, so a stale selection can't survive a provider change.
   *
   * Every path that resolves a REAL provider routes through here. The gate originally landed on the manual
   * pick only, and the programmatic paths (both pre-invoice sources) silently drifted off it — a non-HND
   * provider still got a populated CAI list. Keeping one method means the next such path can't drift again.
   *
   * NOT for the rebuilt "pseudo" providers (a loaded document, or a note's source invoice): those carry no
   * countryCode at all, and must keep showing their saved code — mirrors providerCountryHasNoAuthorization(),
   * which likewise only hides the field on a new document when the domicile is unknown.
   */
  private syncProviderCais(provider: Business | null): void {
    const country = provider?.countryCode;
    if (provider?.id && country && AUTHORIZATION_COUNTRY_OPTIONS.some((c) => c.value === country)) {
      this.loadProviderCais(provider.id);
      return;
    }
    this.form.controls.cai.setValue(null, { emitEvent: false });
    this.caiOptions.set([]);
  }

  /**
   * Loads the provider's active CAIs for the CAI dropdown, narrowed to those that COVER this document
   * type (#4791). An authorization lists the documents it was granted for, so the filter is containment —
   * their ordinary CAI covering Invoice + Receipt shows on both, while a note's shows only on that note.
   * Offering anything wider would let the user pick an authorization the API then rejects on post.
   */
  private loadProviderCais(providerId: string): void {
    const documentType = this.documentTypeSig();
    if (isSelfIssuedApDocument(documentType) || documentType === ApDocumentType.Policy) {
      this.caiOptions.set([]);
      return;
    }

    this.caiService.search(providerId, true, AP_DOCUMENT_ENTITY_DEFINITION_CODE).subscribe({
      next: (response) => {
        const options = (response.data ?? [])
          .filter((c) => c.documentTypes?.includes(documentType))
          .map((c) => ({
            label: `${c.authorizationNumber} (${c.startRange}–${c.endRange})`,
            value: c.authorizationNumber,
          }));
        this.caiOptions.set(options);

        // Drop a selection the new set no longer offers (#5029). Switching an Invoice to a Credit Note
        // narrows the list to CAIs covering notes, and silently keeping the invoice-only code left a field
        // that looked filled in and valid but names an authorization the API rejects on post.
        const current = this.form.controls.cai.value;
        if (current && !options.some((o) => o.value === current)) {
          this.form.controls.cai.setValue(null, { emitEvent: false });
        }
      },
      error: () => this.caiOptions.set([]),
    });
  }

  onProviderCleared(): void {
    this.selectedProvider.set(null);
    this.availablePos.set([]);
    this.caiOptions.set([]);
    // #4785 — the PO picker belongs to the cleared provider; reset its paging and pinned selections too.
    this.addedPoDetails.set(new Map());
    this.poTotalRecords.set(0);
    this.poPage.set(1);
    // #4784 — the term belonged to the cleared provider / source document; don't leave it stranded.
    this.previewPaymentTerm(null, PaymentTermSource.ProviderDefault, null);
    // Only Invoice lines depend on the provider (PO-sourced) — clear them. Free / item-based types keep
    // whatever lines the user already entered (seed an empty one only if none remain).
    if (this.useItemTypeAhead()) {
      this.lines.clear();
      this.refreshAfterLineChange();
    } else if (this.lines.length === 0) {
      this.addLine();
    }
  }

  // ---- pre-invoice (#4306, #4767) ----

  /** Toggles "register from Pre-Invoice" mode. In this mode the provider + lines come from the selected
   * Order Invoice / Receipt (server-resolved on create), so the provider is no longer required up front. */
  setPreInvoiceMode(on: boolean): void {
    this.usePreInvoice.set(on);
    const providerCtrl = this.form.controls.provider;
    if (on) {
      this.onProviderCleared();
      this.form.controls.provider.setValue(null, { emitEvent: false });
      providerCtrl.clearValidators();
    } else {
      this.selectedPreInvoice.set(null);
      providerCtrl.setValidators([Validators.required]);
    }
    providerCtrl.updateValueAndValidity({ emitEvent: false });
  }

  /** #4767 — switches the picker between the two pre-invoice sources. The AP document type is untouched;
   * only the searched collection changes, so any half-made selection is dropped. */
  setPreInvoiceSource(source: PreInvoiceSource): void {
    if (this.preInvoiceSource() === source) return;
    this.preInvoiceSource.set(source);
    this.clearPreInvoice();
  }

  /**
   * One search box, matched against every number the user might have in hand: our own number (invoice /
   * receipt number), the provider's invoice number and the provider's fiscal (CAI) number. The term goes
   * into all three criteria and `matchAny` ORs them server-side; site scope and the consumed exclusion stay
   * AND'd, so the result is still limited to this user's unconsumed documents (#4767).
   */
  public fetchPreInvoices = (query: string): Observable<PreInvoiceOption[]> => {
    // Approved only: a Draft or Submitted source has not been authorised yet, and registering an AP
    // document against one commits the payable before the order side agreed to it. Rejected and Cancelled
    // are excluded for the same reason. The server enforces this too — the filter here is so the officer
    // never sees a row that registration would refuse.
    const statuses = [ORDER_DOCUMENT_STATUS_APPROVED];
    if (this.preInvoiceSource() === 'receipt') {
      return this.receiptOrderService
        .searchUnconsumed({ number: query, providerInvoiceNumber: query, fiscalInvoiceNumber: query, statuses }, 1, 10, true)
        .pipe(map((response) => this.compactOptions((response.data?.items ?? []).map((r) => this.toReceiptOption(r)))));
    }

    return this.invoiceOrderService
      .searchUnconsumed({ invoiceNumber: query, providerInvoiceNumber: query, fiscalInvoiceNumber: query, statuses }, 1, 10, true)
      .pipe(map((response) => this.compactOptions((response.data?.items ?? []).map((i) => this.toInvoiceOption(i)))));
  };

  /** Drops rows with no id — the id is what gets consumed on create, so a row without one is unusable. */
  private compactOptions(options: (PreInvoiceOption | null)[]): PreInvoiceOption[] {
    return options.filter((option): option is PreInvoiceOption => !!option);
  }

  /** Normalizes an Order Invoice into a picker row. Null for a source with no id — the id is what gets
   * consumed on create, so a row without one is not selectable. */
  private toInvoiceOption(invoice: PreInvoiceDto): PreInvoiceOption | null {
    if (!invoice.id) return null;
    return {
      id: invoice.id,
      number: invoice.invoiceNumber ?? '',
      providerInvoiceNumber: invoice.providerInvoiceNumber ?? null,
      fiscalInvoiceNumber: invoice.fiscalInvoiceNumber ?? null,
      totalAmount: invoice.totalAmount ?? 0,
      purchaseOrderNumbers: (invoice.purchaseOrderNumbers ?? []).filter((n): n is string => !!n),
      invoice,
    };
  }

  /** Normalizes an Order Receipt into a picker row (#4767). A receipt has no PO. */
  private toReceiptOption(receipt: PreReceiptDto): PreInvoiceOption | null {
    if (!receipt.id) return null;
    return {
      id: receipt.id,
      number: receipt.number ?? '',
      providerInvoiceNumber: receipt.providerInvoiceNumber ?? null,
      fiscalInvoiceNumber: receipt.fiscalInvoiceNumber ?? null,
      totalAmount: receipt.totalAmount ?? 0,
      purchaseOrderNumbers: [],
      receipt,
    };
  }

  onPreInvoiceSelected(option: PreInvoiceOption): void {
    this.selectedPreInvoice.set(option);
    // #4783 — the AP document date IS the provider's issue date, so take it from the source document rather
    // than leaving it at today's default. The server re-seeds it authoritatively on create; doing it here
    // too means the age warning (and the due date) reflect the real invoice before the user saves.
    this.applySourceIssuedDate(option.receipt?.issuedDate ?? option.invoice?.invoiceIssuedDate ?? null);
    if (option.receipt) {
      this.applyPreReceipt(option.receipt);
      return;
    }
    if (option.invoice) this.applyPreInvoice(option.invoice);
  }

  /** Seeds documentDate from a source document's issue date (#4783). No-op when the source has none. */
  private applySourceIssuedDate(issuedDate: string | null): void {
    if (!issuedDate) return;
    const parsed = new Date(issuedDate);
    if (isNaN(parsed.getTime())) return;
    this.form.controls.documentDate.setValue(parsed);
  }

  private applyPreInvoice(invoice: PreInvoiceDto): void {
    // Batch-fetch ALL the invoice's POs in one call (no N+1) → resolve the single provider + currency, then
    // populate the document (provider, lines, currency, provider invoice #) client-side for review before
    // saving. The server re-resolves + re-checks on create (single source of truth).
    const poRefs = (invoice.purchaseOrderReferences ?? []).filter((ref): ref is string => !!ref);
    if (poRefs.length === 0) return;
    this.purchaseOrderService.getByIds(poRefs).subscribe({
      next: (poResponse) => {
        const pos = poResponse.data ?? [];
        const providerRefs = Array.from(new Set(pos.map((po) => po.providerReference).filter((ref): ref is string => !!ref)));
        if (providerRefs.length !== 1) {
          GlobalMessageUtil.warn(
            this.t('ApDocument_PreInvoice_MultiProvider_Title', 'Single provider required'),
            this.t('ApDocument_PreInvoice_MultiProvider', "The pre-invoice's purchase orders must resolve to a single provider."),
          );
          this.clearPreInvoice();
          return;
        }
        const currencies = Array.from(new Set(pos.map((po) => po.currency).filter((c): c is number => c !== null && c !== undefined)));
        if (currencies.length > 1) {
          GlobalMessageUtil.warn(
            this.t('ApDocument_PreInvoice_MultiCurrency_Title', 'Single currency required'),
            this.t('ApDocument_PreInvoice_MultiCurrency', "The pre-invoice's purchase orders must share a single currency."),
          );
          this.clearPreInvoice();
          return;
        }
        this.businessService.getBusinessById(providerRefs[0]).subscribe({
          next: (bizResponse) => {
            const provider = bizResponse.data;
            if (!provider) return;
            // Reuse the provider AP-account guard — block the pre-invoice if the GL account is missing.
            if (!this.ensureProviderHasApAccount(provider)) {
              this.clearPreInvoice();
              return;
            }
            this.selectedProvider.set(provider);
            // #4784 — an Order invoice has no term of its own; it inherits the originating PO's. The POs of
            // one pre-invoice share a provider and are expected to share a term, so the first that carries
            // one wins (same rule the server applies).
            this.previewPaymentTerm(
              pos.map((po) => po.paymentTermCode).find((code): code is string => !!code?.trim()) ?? null,
              PaymentTermSource.PurchaseOrder,
              provider.providerDetails?.paymentTermsCode ?? null,
            );
            this.recomputeDueDate();
            // Load the resolved provider's CAIs — the pre-invoice path sets the provider programmatically,
            // so (unlike onProviderSelected) the CAI dropdown wouldn't populate otherwise. Gated on the
            // domicile (#4726): an Order invoice from a provider outside Honduras carries no CAI.
            this.syncProviderCais(provider);
            // Currency from the PO — single across the invoice's POs, non-changeable in pre-invoice mode.
            if (currencies.length === 1) {
              this.form.controls.currency.setValue(currencies[0], { emitEvent: false });
              this.form.controls.currency.disable({ emitEvent: false });
              this.loadRateInForce(currencies[0]);
            }
            // #4785 — the Order invoice's own rate wins over the day's rate on the API side (#4306); show it
            // so the functional amount on screen matches what will actually post.
            this.applySourceExchangeRate(invoice.exchangeRateToUSD ?? null, 'invoice');
            // Order keeps its own field names; each maps to its counterpart here and the two never cross.
            // Its fiscalInvoiceNumber is the CAI number that gets range-checked on post, so it fills our
            // fiscalNumber; its providerInvoiceNumber is the provider's own ERP number, so it fills our
            // providerReferenceNumber, which nothing validates. No fallback between them.
            this.form.controls.fiscalNumber.setValue(invoice.fiscalInvoiceNumber ?? null, { emitEvent: false });
            this.form.controls.providerReferenceNumber.setValue(invoice.providerInvoiceNumber ?? null, { emitEvent: false });
            // Lines: quantity from the invoice, price (locked) from the matching PO item.
            this.buildPreInvoiceLines(invoice, pos);
            // #5165 — the received date belongs to this invoice's shipment. Fetched now so the officer sees
            // the date the save will store, rather than a blank read-only field they cannot fill.
            this.loadShipmentReceivedDate(invoice.id ?? null);
          },
        });
      },
    });
  }

  /**
   * #4767 — populates the document from an Order Receipt. Unlike an invoice, a receipt is not tied to a PO:
   * the provider, currency and prices all come straight off the receipt, so there is nothing to reconcile
   * against a PO and no multi-provider / multi-currency case to guard. The server re-resolves the same way
   * on create (single source of truth) and takes the exchange rate from the receipt.
   */
  private applyPreReceipt(receipt: PreReceiptDto): void {
    if (!receipt.providerReference) {
      GlobalMessageUtil.warn(
        this.t('ApDocument_PreReceipt_NoProvider_Title', 'Provider required'),
        this.t('ApDocument_PreReceipt_NoProvider', 'The receipt has no provider, so an AP document cannot be registered from it.'),
      );
      this.clearPreInvoice();
      return;
    }
    this.businessService.getBusinessById(receipt.providerReference).subscribe({
      next: (response) => {
        const provider = response.data;
        if (!provider) return;
        // Reuse the provider AP-account guard — block the pre-invoice if the GL account is missing.
        if (!this.ensureProviderHasApAccount(provider)) {
          this.clearPreInvoice();
          return;
        }
        this.selectedProvider.set(provider);
        // The receipt's own kind decides the AP document kind, so switch the form over before anything else
        // paints: a Fiscal receipt is an AP Receipt (the provider's fiscal number + CAI are transcribed
        // here), a Purchase receipt is an AP Purchase Receipt (self-issued number, so isSelfIssued hides
        // those inputs and shows the next-number preview instead). The route's ?type= is only a guess — the
        // user enters this flow before picking a receipt — so it must not survive the pick.
        this.applyReceiptDocumentType(receipt.type ?? null);
        // #4784 — the receipt's own term beats the provider default; show that immediately, not after save.
        this.previewPaymentTerm(
          receipt.paymentTermCode ?? null,
          PaymentTermSource.Receipt,
          provider.providerDetails?.paymentTermsCode ?? null,
        );
        this.recomputeDueDate();
        // CAI only applies to Honduras-domiciled providers (#4726); the pre-invoice path sets the provider
        // programmatically, so (unlike onProviderSelected) the dropdown wouldn't populate otherwise.
        this.syncProviderCais(provider);
        // Currency from the receipt — a receipt carries its own, and it's locked like the invoice path.
        if (receipt.currency !== null && receipt.currency !== undefined) {
          this.form.controls.currency.setValue(receipt.currency, { emitEvent: false });
          this.form.controls.currency.disable({ emitEvent: false });
          this.loadRateInForce(receipt.currency);
        }
        // #4785 — the receipt's own rate wins over the day's rate on the API side (#4767); show it so the
        // functional amount on screen matches what will actually post.
        this.applySourceExchangeRate(receipt.exchangeRateToUSD ?? null, 'receipt');
        // Numbers from the receipt (fiscal receipts only — a purchase receipt has none). Order keeps its own
        // names and each maps to its counterpart: fiscalInvoiceNumber is the CAI number range-checked on
        // post, providerInvoiceNumber is the provider's own ERP number. No fallback between them.
        this.form.controls.fiscalNumber.setValue(receipt.fiscalInvoiceNumber ?? null, { emitEvent: false });
        this.form.controls.providerReferenceNumber.setValue(receipt.providerInvoiceNumber ?? null, { emitEvent: false });
        this.buildPreReceiptLines(receipt);
      },
    });
  }

  /**
   * Switches the form to the AP document kind the picked receipt implies — Fiscal → Receipt,
   * Purchase → Purchase Receipt. The API derives the same thing from the receipt and is the authority; this
   * only keeps the form honest while the clerk fills it in, so the fiscal-number and CAI inputs appear only
   * for the kind that actually carries them.
   *
   * An unrecognised (or absent) type is left alone rather than guessed — the API will reject it, and
   * silently registering it as an Invoice is exactly the behaviour being fixed.
   */
  private applyReceiptDocumentType(type: ReceiptType | null): void {
    const documentType =
      type === ReceiptType.Fiscal ? ApDocumentType.Receipt : type === ReceiptType.Purchase ? ApDocumentType.PurchaseReceipt : null;
    if (documentType === null || documentType === this.documentTypeSig()) return;

    this.documentTypeSig.set(documentType);
    this.form.controls.documentType.setValue(documentType, { emitEvent: false });
    this.headerTitle.set(
      this.isPurchaseReceipt()
        ? this.t('ApDocument_NewPurchaseReceipt', 'New Purchase Receipt')
        : this.t('ApDocument_NewReceipt', 'New Receipt'),
    );
    // A Purchase Receipt's fiscal number is drawn from the company's CAI on Post; show the same expected
    // next-number banner the standalone Purchase Receipt flow shows.
    if (this.isPurchaseReceipt()) this.loadPurchaseReceiptPreview();
  }

  /** Builds the AP lines from the receipt's items. The receipt prices per line total, so the AP unit price
   * is total ÷ quantity — the same derivation the API applies when it rebuilds the lines itself (#4767). */
  private buildPreReceiptLines(receipt: PreReceiptDto): void {
    this.lines.clear();
    for (const item of receipt.items ?? []) {
      // Only the item code is known here; resolveLineItems below swaps in the full item master.
      const group = this.buildLineGroup(item.itemCode ? this.pseudoItem(item.itemCode, item.description ?? item.itemCode) : undefined);
      const quantity = item.quantity ?? 0;
      const total = item.total ?? 0;
      group.patchValue(
        {
          description: item.description ?? item.itemCode ?? null,
          uom: item.unitOfMeasure ?? null,
          quantity: quantity || 1,
          unitPrice: quantity ? Math.round((total / quantity) * 10000) / 10000 : total,
          taxRate: item.taxRate ?? 0,
          // #4842 — the receipt already carries the classification, so a receipt-sourced line arrives
          // classified. patchValue reaches a disabled control, which is what we want here: the value is the
          // receipt's, and the clerk does not get to change it.
          itemClassificationCode: item.itemClassification ?? null,
        },
        { emitEvent: false },
      );
      // #4890 — buildLineGroup only locks the classification for a line that carries an item, so a
      // description-only receipt line arrived classified by the receipt but editable, and the clerk could
      // overwrite what the receipt already decided. The receipt is the authority wherever it stated one;
      // a line it left unclassified stays open so the clerk can still supply the required value.
      if (item.itemClassification) group.controls.itemClassificationCode.disable({ emitEvent: false });
      // Preview only: qty + price are locked; the user only adds the GL account per line.
      group.controls.quantity.disable({ emitEvent: false });
      group.controls.unitPrice.disable({ emitEvent: false });
      this.lines.push(group);
    }
    this.refreshAfterLineChange();
    // Hydrate the full item master (by code) so each line's GL picker can be limited to the item's ledgers.
    this.resolveLineItems(this.lines.controls.map((l) => ({ itemCode: l.controls.itemSelection.value?.itemCode }) as ApDocumentLine));
  }

  /** Builds the AP lines from the invoice's items, pricing each from its matching PO item (#4306). */
  private buildPreInvoiceLines(invoice: PreInvoiceDto, pos: PurchaseOrderDto[]): void {
    const poItemById = new Map<string, { po: PurchaseOrderDto; item: ItemInPurchaseOrderDto }>();
    for (const po of pos) {
      for (const item of po.items ?? []) {
        if (item.id) poItemById.set(item.id, { po, item });
      }
    }
    this.lines.clear();
    for (const invItem of invoice.items ?? []) {
      const match = invItem.itemInPurchaseOrderId ? poItemById.get(invItem.itemInPurchaseOrderId) : undefined;
      const group = this.buildLineGroup(match?.item.item ?? undefined, match?.item, match?.po);
      // Quantity comes from the invoice (invoiced qty), not the PO (ordered qty).
      group.controls.quantity.setValue(invItem.quantity ?? match?.item.quantity ?? 1, { emitEvent: false });
      // #6537 — keep the invoice's PO item id even when its PO did not resolve (as the API's own fallback
      // does), so the line stays a seeded line and is not taken for a charge.
      if (!match && invItem.itemInPurchaseOrderId)
        group.controls.purchaseOrderItemId.setValue(invItem.itemInPurchaseOrderId, { emitEvent: false });
      // Preview only: qty + price are locked; the user only adds the GL account per line.
      group.controls.quantity.disable({ emitEvent: false });
      group.controls.unitPrice.disable({ emitEvent: false });
      this.lines.push(group);
    }
    this.refreshAfterLineChange();
    // Hydrate the full item master (by code) so each line's GL picker can be limited to the item's ledgers.
    this.resolveLineItems(this.lines.controls.map((l) => ({ itemCode: l.controls.itemSelection.value?.itemCode }) as ApDocumentLine));
  }

  private clearPreInvoice(): void {
    this.selectedPreInvoice.set(null);
    this.selectedProvider.set(null);
    this.lines.clear();
    this.refreshAfterLineChange();
    this.form.controls.currency.enable({ emitEvent: false });
    this.form.controls.fiscalNumber.setValue(null, { emitEvent: false });
    this.form.controls.providerReferenceNumber.setValue(null, { emitEvent: false });
    // #4785 — no source document, so the day's rate governs again.
    this.sourceExchangeRate.set(null);
    this.sourceRateOrigin.set(null);
    // #5165 — the date came from the cleared invoice's shipment; it does not belong to whatever comes next.
    this.form.controls.receivedDate.setValue(null, { emitEvent: false });
  }

  onPreInvoiceCleared(): void {
    this.clearPreInvoice();
  }

  get providerTaxId(): string {
    // Authoritative business/provider tax id lives on Business.TaxIndicatorCode.
    return this.selectedProvider()?.taxIndicatorCode ?? '';
  }

  get providerCreditDays(): number {
    return this.selectedProvider()?.providerDetails?.creditDays ?? 0;
  }

  /**
   * #4784 — credit days used for the due-date display. Prefers the server-resolved value (the source PO's
   * or Receipt's term may beat the provider default), falling back to the provider default for the pre-save
   * preview. The UI never DERIVES credit days from a term code — that stays in PaymentTermsHelper API-side;
   * both of these numbers are ones the API already computed.
   */
  get effectiveCreditDays(): number {
    return this.resolvedCreditDays() ?? this.providerCreditDays;
  }

  private recomputeDueDate(): void {
    const docDate = this.form.controls.documentDate.value;
    if (!docDate) return;
    const due = new Date(docDate);
    due.setDate(due.getDate() + this.effectiveCreditDays);
    this.form.controls.dueDate.setValue(due, { emitEvent: false });
  }

  /**
   * #4784 — client-side PREVIEW of the payment term, shown the moment a provider or source document is
   * picked so the field isn't blank until the first save. Mirrors the server's precedence (source document
   * beats provider default) but is not authoritative: `applyResolvedPaymentTerm` overwrites it with what
   * the API actually resolved on create/update.
   *
   * Note it deliberately does NOT touch `resolvedCreditDays` — mapping a term code to a day count lives in
   * PaymentTermsHelper API-side, so the due-date preview stays on the provider's credit days and corrects
   * itself on save.
   */
  private previewPaymentTerm(sourceCode: string | null, source: PaymentTermSource, providerDefault: string | null): void {
    if (this.isCreditNote()) {
      this.previewCreditNoteTerm();
      return;
    }
    const effective = sourceCode?.trim() || providerDefault?.trim() || null;
    this.paymentTermCode.set(effective);
    this.paymentTermSource.set(sourceCode?.trim() ? source : PaymentTermSource.ProviderDefault);
    this.providerPaymentTermCode.set(providerDefault?.trim() || null);
    this.form.controls.paymentTermCode.setValue(effective, { emitEvent: false });
    // Credit days for the previewed term come from the API's catalog — the UI looks the code up, it never
    // decides what a code is worth. Unknown / missing code falls back to the provider's own credit days.
    this.resolvedCreditDays.set(effective ? (this.creditDaysByTerm().get(effective) ?? null) : null);
    // A new provider / source document re-establishes the authoritative term, so any prior override lapses.
    this.paymentTermOverrideOn.set(false);
    this.form.controls.paymentTermCode.disable({ emitEvent: false });
    this.recomputeDueDate();
  }

  /** #6324 — a credit note is applied, never paid: no term, 0 credit days, due on its own date (as the API). */
  private previewCreditNoteTerm(): void {
    this.paymentTermCode.set(null);
    this.paymentTermSource.set(PaymentTermSource.RelatedDocument);
    this.resolvedCreditDays.set(0);
    this.form.controls.paymentTermCode.setValue(null, { emitEvent: false });
    this.paymentTermOverrideOn.set(false);
    this.form.controls.paymentTermCode.disable({ emitEvent: false });
    this.recomputeDueDate();
  }

  /** #4784 — adopt the payment term the server resolved (create / update / load all land here). */
  private applyResolvedPaymentTerm(data: ApDocument): void {
    this.paymentTermCode.set(data.paymentTermCode ?? null);
    this.paymentTermSource.set(data.paymentTermSource ?? PaymentTermSource.ProviderDefault);
    this.providerPaymentTermCode.set(data.providerPaymentTermCode ?? null);
    this.resolvedCreditDays.set(data.creditDays ?? null);
    this.form.controls.paymentTermCode.setValue(data.paymentTermCode ?? null, { emitEvent: false });
  }

  /** #4784 — where the resolved term came from, for the badge next to the field. */
  get paymentTermSourceLabel(): string {
    switch (this.paymentTermSource()) {
      case PaymentTermSource.PurchaseOrder:
        return this.t('ApDocument_PaymentTerm_FromPo', 'from purchase order');
      case PaymentTermSource.Receipt:
        return this.t('ApDocument_PaymentTerm_FromReceipt', 'from receipt');
      case PaymentTermSource.RelatedDocument:
        return this.t('ApDocument_PaymentTerm_FromOriginal', 'from original document');
      case PaymentTermSource.Manual:
        return this.t('ApDocument_PaymentTerm_Manual', 'overridden');
      default:
        return this.t('ApDocument_PaymentTerm_FromProvider', 'provider default');
    }
  }

  /** #4784 — explains why the field is locked and where the term must be corrected instead. */
  get paymentTermTooltip(): string {
    return this.paymentTermFromSourceDocument()
      ? this.t(
          'ApDocument_PaymentTerm_Tooltip_Source',
          'This payment term comes from the originating Receipt / Purchase Order. To change it, edit the payment term on that document — it cannot be overridden here.',
        )
      : this.t(
          'ApDocument_PaymentTerm_Tooltip_Provider',
          "This payment term is the provider's default. To change it, edit the payment terms on the provider's master record — it cannot be overridden here.",
        );
  }

  // ---- item (global item-master search — kept for non-PO document types) ----
  public fetchItems = (query: string): Observable<ItemMasterDto[]> =>
    this.itemService.postItemSearch({ itemCode: query, name: query }, true).pipe(map((response) => response.data?.items ?? []));

  // ---- purchase orders (multi-PO, line-driven) ----
  /** Standard always; Administrative too when the user holds the role (#5942). */
  private purchaseOrderTypesForPicker(): number[] {
    return this.auth.roles.includes(AdministrativePurchaseOrderRoles.AdministrativePurchaseOrderAdmin)
      ? [PURCHASE_ORDER_TYPE_STANDARD, PURCHASE_ORDER_TYPE_ADMINISTRATIVE]
      : [PURCHASE_ORDER_TYPE_STANDARD];
  }

  /** Loads the provider's open POs for the line-items picker. Requires a provider. An optional PO-number
   * search is passed through to Order.API so matches beyond the first page are found (not only the
   * preloaded set); empty search loads the first page. */
  private loadProviderPurchaseOrders(providerId: string, search?: string | null, page = 1): void {
    const purchaseOrderNumber = search?.trim() ? search.trim() : null;
    // Invoices draw Standard POs (Yarn/Chemicals are handled by their own flows), plus Administrative ones for
    // role holders (#5942) — Order.API would hide those from anyone else anyway. Paged (#4785) — the PO-number
    // search still re-queries the server, but the rest are now reachable by paging.
    this.poPage.set(page);
    this.purchaseOrderService
      .search({ providerReference: providerId, types: this.purchaseOrderTypesForPicker(), purchaseOrderNumber }, page, this.poPageSize)
      .subscribe({
        // Cancelled POs can't be invoiced against — keep them out of the picker (#4516).
        next: (response) => {
          this.availablePos.set((response.data?.items ?? []).filter((po) => po.status !== PURCHASE_ORDER_STATUS_CANCELLED));
          this.poTotalRecords.set(response.data?.totalCount ?? 0);
        },
        error: () => {
          this.availablePos.set([]);
          this.poTotalRecords.set(0);
        },
      });
  }

  /** #4785 — paginator handler for the PO picker. */
  onPoPageChange(event: { first?: number; rows?: number }): void {
    const providerId = this.selectedProvider()?.id;
    if (!providerId) return;
    const rows = event.rows ?? this.poPageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    this.loadProviderPurchaseOrders(providerId, this.poSearchControl.value, page);
  }

  poTotal(po: PurchaseOrderDto): number {
    return (po.items ?? []).reduce((acc, it) => acc + (it.totalPrice ?? (it.quantity ?? 0) * (it.unitPrice ?? 0)), 0);
  }

  poItemCount(po: PurchaseOrderDto): number {
    return (po.items ?? []).length;
  }

  isPoAdded(poId: string | null | undefined): boolean {
    return !!poId && this.addedPoIds().has(poId);
  }

  /** Adds every item of a PO as a (price-locked) line, stamped with the PO key + item ref. */
  addPurchaseOrder(po: PurchaseOrderDto): void {
    if (!po?.id || this.isPoAdded(po.id)) return;
    this.startLoading();
    this.purchaseOrderService
      .getById(po.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.appendPurchaseOrderLines(response.data ?? po),
        error: () => this.appendPurchaseOrderLines(po),
      });
  }

  private appendPurchaseOrderLines(po: PurchaseOrderDto): void {
    // #4785 — remember the full PO so it stays listed (and keeps its date / totals) once the user searches
    // or pages past it.
    if (po.id) this.addedPoDetails.update((m) => new Map(m).set(po.id!, po));
    if (!po.id) return;
    // The first PO sets the document currency (all lines share it).
    if (this.lines.length === 0 && po.currency !== null && po.currency !== undefined) {
      this.form.controls.currency.setValue(po.currency, { emitEvent: false });
      this.loadRateInForce(po.currency);
    }
    for (const poItem of po.items ?? []) {
      const item = poItem.item ?? this.pseudoItem(poItem.itemCode ?? '', this.poItemLabel(poItem));
      this.lines.push(this.buildLineGroup(item, poItem, po));
    }
    this.refreshAfterLineChange();
    // #4724 — how much of each item was already invoiced on OTHER documents (drives the indicator +
    // block), and pre-fill each new line's qty with what's left to invoice.
    this.loadPoInvoicedSummary(po.id, true);
  }

  /** #4724 — fetch the per-PO-item invoiced-vs-ordered rollup for a PO (excluding this document) and merge
   * it into the map. Non-blocking: on error the notes simply stay off. When applyRemainingDefault is set
   * (a freshly added PO), each new line's qty is pre-filled with the remaining invoiceable quantity. */
  private loadPoInvoicedSummary(purchaseOrderReference: string, applyRemainingDefault = false): void {
    if (!purchaseOrderReference) return;
    this.service.getPoInvoicedSummary(purchaseOrderReference, this.currentId()).subscribe({
      next: (response) => {
        const next = new Map(this.poInvoicedByItem());
        for (const info of response.data ?? []) {
          if (info.purchaseOrderItemReference) next.set(info.purchaseOrderItemReference, info);
        }
        this.poInvoicedByItem.set(next);
        if (applyRemainingDefault) this.applyRemainingQtyDefaults(purchaseOrderReference);
      },
      error: () => undefined,
    });
  }

  /** #4724 — pre-fill each freshly added line's qty with the remaining invoiceable quantity (ordered −
   * invoiced elsewhere) so the user invoices exactly what's left. Only applied when remaining > 0; a fully
   * invoiced item keeps its ordered qty so the over-invoice indicator flags it (nothing left to add). */
  private applyRemainingQtyDefaults(purchaseOrderReference: string): void {
    for (const line of this.lines.controls) {
      if (line.controls.purchaseOrderReference.value !== purchaseOrderReference) continue;
      const info = this.lineInvoicedInfo(line);
      if (!info) continue;
      const remaining = Math.round((info.orderedQuantity - info.invoicedQuantity) * 10000) / 10000;
      if (remaining > 0) line.controls.quantity.setValue(remaining, { emitEvent: false });
    }
    this.recomputeTotals();
  }

  /** #4724 — hover tooltip for the per-line invoiced indicator: ordered, already invoiced elsewhere, and
   * remaining; plus by how much the current qty exceeds what's left, when over. */
  lineInvoicedTooltip(line: FormGroup<ApDocumentLineFormGroup>): string {
    const info = this.lineInvoicedInfo(line);
    if (!info) return '';
    const fmt = (n: number) => (Math.round(n * 10000) / 10000).toLocaleString();
    const remaining = info.orderedQuantity - info.invoicedQuantity;
    const parts = [
      `${this.t('ApDocument_Line_Ordered', 'Ordered')}: ${fmt(info.orderedQuantity)}`,
      `${this.t('ApDocument_Line_AlreadyInvoiced', 'Already invoiced')}: ${fmt(info.invoicedQuantity)}`,
      `${this.t('ApDocument_Line_Remaining', 'Remaining')}: ${fmt(remaining)}`,
    ];
    if (this.lineOverInvoiced(line)) {
      parts.push(`${this.t('ApDocument_Line_ExceedsBy', 'Exceeds by')}: ${fmt((line.controls.quantity.value ?? 0) - remaining)}`);
    }
    return parts.join(' · ');
  }

  /** The already-invoiced-elsewhere info for a line's PO item, once the rollup is loaded. */
  lineInvoicedInfo(line: FormGroup<ApDocumentLineFormGroup>): PoItemInvoiced | null {
    const ref = line.controls.purchaseOrderItemId.value;
    return ref ? (this.poInvoicedByItem().get(ref) ?? null) : null;
  }

  /** #4724 — quantity this document may still invoice for the line's PO item (ordered − invoiced
   * elsewhere). Null when there is no PO-item rollup for the line (free/charge lines). */
  lineRemainingQuantity(line: FormGroup<ApDocumentLineFormGroup>): number | null {
    const info = this.lineInvoicedInfo(line);
    return info ? info.orderedQuantity - info.invoicedQuantity : null;
  }

  /** #4724 — true when the line's quantity would push its PO item past the ordered quantity. Only marks the
   * line: whether that refuses the save is the API's call (#6538, FeatureFlags:EnforcePoItemQuantityCap). */
  lineOverInvoiced(line: FormGroup<ApDocumentLineFormGroup>): boolean {
    const remaining = this.lineRemainingQuantity(line);
    if (remaining === null) return false;
    return (line.controls.quantity.value ?? 0) > remaining + 0.0001;
  }

  removePurchaseOrder(poId: string): void {
    for (let i = this.lines.length - 1; i >= 0; i--) {
      if (this.lines.at(i).controls.purchaseOrderReference.value === poId) this.lines.removeAt(i);
    }
    this.refreshAfterLineChange();
  }

  /** Puts back the PO lines the user removed from a group — and touches nothing else. Removing a line was
   * the one edit with no undo: the only way back was removing and re-adding the whole PO, which threw away
   * every qty and price typed on the lines that were never wrong. The PO is re-fetched rather than read
   * from the added-PO cache, which on a reloaded draft can hold a list row without its items. */
  resetPurchaseOrderLines(poId: string): void {
    if (!poId) return;
    this.startLoading();
    this.purchaseOrderService
      .getById(poId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.restoreRemovedPoLines(response.data),
        error: (error) => console.error('Error restoring purchase order lines:', error),
      });
  }

  /** Adds a line for every PO item its group no longer carries (matched by PO-item id), at the same
   * defaults addPurchaseOrder would give it — including the remaining-to-invoice qty prefill, read from
   * the already-loaded rollup. Deliberately NOT applyRemainingQtyDefaults: that sweeps the whole group
   * and would overwrite the quantities the user typed on the lines that were never removed. */
  private restoreRemovedPoLines(po?: PurchaseOrderDto | null): void {
    if (!po?.id) return;
    const present = new Set(
      this.lines.controls
        .filter((line) => line.controls.purchaseOrderReference.value === po.id)
        .map((line) => line.controls.purchaseOrderItemId.value),
    );
    const restored: FormGroup<ApDocumentLineFormGroup>[] = [];
    for (const poItem of po.items ?? []) {
      if (!poItem.id || present.has(poItem.id)) continue;
      const item = poItem.item ?? this.pseudoItem(poItem.itemCode ?? '', this.poItemLabel(poItem));
      const line = this.buildLineGroup(item, poItem, po);
      this.lines.push(line);
      restored.push(line);
    }
    if (restored.length === 0) return;
    for (const line of restored) {
      const info = this.lineInvoicedInfo(line);
      if (!info) continue;
      const remaining = Math.round((info.orderedQuantity - info.invoicedQuantity) * 10000) / 10000;
      if (remaining > 0) line.controls.quantity.setValue(remaining, { emitEvent: false });
    }
    this.refreshAfterLineChange();
  }

  poItemLabel(poItem: ItemInPurchaseOrderDto): string {
    return poItem.item ? this.itemLongName(poItem.item) : (poItem.itemCode ?? '');
  }

  /** Recomputes totals, the added-PO set and the grouped view after any line add/remove. */
  private refreshAfterLineChange(): void {
    this.recomputeTotals();
    this.recomputeAddedPos();
    this.recomputeGroups();
    this.onLineClassificationChanged();
    // #5165 — adding a PO can introduce a receiving date the clerk should be able to pick. Guarded inside
    // against the PO set being unchanged, since this runs on every single line edit.
    this.loadReceivedDateOptions();
  }

  /** #4842 — nudges the live withholding preview. Called whenever the set of lines changes and from the
   * classification picker, since a FormArray is not itself reactive to signals. */
  onLineClassificationChanged(): void {
    // The classification is what decides goods-vs-service, so the account a goods line posts to has to be
    // re-derived whenever it moves.
    this.applyGlAccountRules();
    this.lineClassificationVersion.update((version) => version + 1);
  }

  /**
   * Fills in the GL account on every goods (Bien) line that has one account to fill in.
   *
   * <p>A goods item posts to exactly one account, so asking a clerk to pick it out of a list of one is a
   * step that can only be got wrong. Service lines are left entirely alone — they choose from the whole
   * chart of accounts.</p>
   *
   * <p><b>An account already on a line is never overwritten</b> (unless <paramref name="refreshDerived"/>).
   * That assignment belongs to the DOCUMENT: a draft reopened after the item master changed must keep
   * posting where it was written to post, or reopening a draft would silently re-book it.</p>
   *
   * <p>Runs silently. It is called from inside a lines subscription, and an emitting write would land back
   * in the handler that triggered it.</p>
   */
  private applyGlAccountRules(refreshDerived = false): void {
    if (!this.isDraft()) return;

    for (const line of this.lines.controls) {
      if (this.lineIsService(line)) continue;

      const account = this.singleItemGlAccount(line);
      if (!account) continue;
      if (line.controls.glAccountReference.value && !refreshDerived) continue;
      if (line.controls.glAccountReference.value === account.reference) continue;

      // Number and name first and silently; the reference LAST and emitting, because that is the control
      // the picker watches — by the time it reacts, the rest of the line already agrees with it. The name
      // is cleared rather than carried over: it belongs to whichever account was there before, and the
      // picker re-reads it from the reference.
      line.controls.glAccountNumber.setValue(account.number, { emitEvent: false });
      line.controls.glAccountName.setValue(null, { emitEvent: false });
      line.controls.glAccountReference.setValue(account.reference);
    }
  }

  private recomputeAddedPos(): void {
    const ids = new Set<string>();
    for (const line of this.lines.controls) {
      const ref = line.controls.purchaseOrderReference.value;
      if (ref) ids.add(ref);
    }
    this.addedPoIds.set(ids);

    // #4785 — keep the pinned-PO details in step with what's actually on the document. A PO removed from the
    // lines stops being pinned; one that is still there but whose details we never loaded (a draft opened
    // fresh) gets a stub from its line, so it renders in the picker instead of silently vanishing.
    const details = new Map(this.addedPoDetails());
    for (const id of Array.from(details.keys())) if (!ids.has(id)) details.delete(id);
    for (const line of this.lines.controls) {
      const ref = line.controls.purchaseOrderReference.value;
      if (!ref || details.has(ref)) continue;
      const known = this.availablePos().find((po) => po.id === ref);
      details.set(ref, known ?? { id: ref, purchaseOrderNumber: line.controls.purchaseOrderNumber.value ?? '' });
    }
    this.addedPoDetails.set(details);
  }

  private recomputeGroups(): void {
    const groups: PoGroup[] = [];
    const byRef = new Map<string, PoGroup>();
    const charges: { index: number; group: FormGroup<ApDocumentLineFormGroup> }[] = [];
    this.lines.controls.forEach((line, index) => {
      const ref = line.controls.purchaseOrderReference.value ?? '';
      // #4725 — on PO-driven Invoices, a line with no PO reference is a charge/fee: pull it out of the PO
      // groups and into its own section. Free-line document types (Receipt/…) keep every line in a single
      // group (they have no PO lines to separate from). #4767 — so does a receipt-sourced pre-invoice: none
      // of its lines carry a PO, and they are the document's real lines, not charges.
      // #6537 — an Order-Invoice-sourced pre-invoice has charges too. A seeded line whose PO did not resolve
      // still carries its PO item id, so it stays with the seeded lines rather than becoming a charge.
      if (this.allowsCharges() && !ref && !(this.isPreInvoice() && line.controls.purchaseOrderItemId.value)) {
        charges.push({ index, group: line });
        return;
      }
      let group = byRef.get(ref);
      if (!group) {
        group = { reference: ref, number: line.controls.purchaseOrderNumber.value ?? '', lines: [] };
        byRef.set(ref, group);
        groups.push(group);
      }
      group.lines.push({ index, group: line });
    });
    this.poGroups.set(groups);
    this.chargeLines.set(charges);
  }

  // ---- exchange ----
  /** #4785 — record the source document's rate (ignoring a missing/zero one, which the API also skips). */
  private applySourceExchangeRate(rate: number | null, origin: 'invoice' | 'receipt'): void {
    if (rate === null || rate <= 0) {
      this.sourceExchangeRate.set(null);
      this.sourceRateOrigin.set(null);
      return;
    }
    this.sourceExchangeRate.set(rate);
    this.sourceRateOrigin.set(origin);
  }

  private loadRateInForce(currency: number | null): void {
    // The document's own date is the one that prices it. This used to ask for today's rate, which agreed
    // with the posting only for a document registered the day it was dated — every backdated invoice was
    // previewed at one rate and booked at another, with nothing on screen admitting it.
    const asOf = this.form.controls.documentDate.value ?? new Date();
    this.rateAsOfDate.set(asOf);

    if (currency === null || currency === undefined) {
      this.rateInForce.set(null);
      this.rateUnavailable.set(false);
      return;
    }
    // USD is the functional/pivot currency — its rate to USD is always 1:1, so there's no rate to look up.
    if (currency === Currency.USD) {
      this.rateInForce.set(1);
      this.rateUnavailable.set(false);
      return;
    }
    this.rateLoading.set(true);
    this.exchangeRateService
      .getRateInForce(currency, asOf)
      .pipe(finalize(() => this.rateLoading.set(false)))
      .subscribe({
        // The average (#5221) — the same figure CurrencyConverter books at, so the preview cannot promise
        // one rate and the posting apply another. rateToUSD is the legacy buy/compra side; it only serves
        // as a fallback for a response predating the field.
        next: (response) => {
          const rate = response.data?.averageRateToUSD ?? response.data?.rateToUSD ?? null;
          this.rateInForce.set(rate);
          this.rateUnavailable.set(rate === null);
        },
        // Nothing on or before that date. A state the panel reports — with the reload beside it — rather
        // than an empty readout the clerk has to interpret.
        error: () => {
          this.rateInForce.set(null);
          this.rateUnavailable.set(true);
        },
      });
  }

  // ---- load existing ----
  private loadDocument(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const data = response.data;
          if (data) {
            this.patchFromDocument(data);
            this.loadPayableDetail(data);
          }
        },
        error: (error) => {
          console.error('Error loading AP document:', error);
          // 404 covers both a missing document and one that draws on an Administrative purchase order the
          // caller may not open (#5942): the API deliberately does not tell them apart. The error interceptor
          // already shows the API's message, so only leave the page.
          if (error?.status === 404) {
            this.router.navigate(['/ap-documents']);
          }
        },
      });
  }

  /** Fetch the resumen (notes + withholding + net) for a posted base document (#4516). Skipped for drafts and
   * for notes themselves (a note has no notes of its own); the panel is further gated by showResumen. */
  private loadPayableDetail(data: ApDocument): void {
    this.payableDetail.set(null);
    if (!data.id || data.status === ApDocumentStatus.Draft || NOTE_AP_DOCUMENT_TYPES.includes(data.documentType)) return;
    this.service.getPayableDetail(data.id).subscribe({
      next: (response) => this.payableDetail.set(response.data ?? null),
      error: () => this.payableDetail.set(null),
    });
  }

  private patchFromDocument(data: ApDocument): void {
    this.status.set(data.status);
    this.attachments.set(data.attachments ?? []);
    this.documentNumber.set(data.number ?? '');
    this.documentTypeSig.set(data.documentType);
    this.headerTitle.set(`${this.t('ApDocument', 'AP Document')} ${data.number ?? ''}`.trim());
    // Pre-Invoice badge (#4306): show the source Order Invoice / Receipt number when this doc was
    // registered from one — the entity code says which of the two it was (#4767).
    this.preInvoiceNumber.set(data.preInvoiceReference?.number ?? null);
    this.loadedFromReceipt.set(data.preInvoiceReference?.entityDefinitionCode === ORDER_RECEIPT_ENTITY_CODE);
    // Debit/Credit Note badge (#4473/#4477): the original invoice this note adjusts.
    this.noteSourceNumber.set(data.relatedDocumentReference?.number ?? null);
    this.noteSourceReference.set(data.relatedDocumentReference?.reference ?? null);
    // #6324 — the reason, and the original the summary and warnings read.
    this.noteReason.set(data.noteReason ?? null);
    this.loadNoteCauses();
    if (NOTE_AP_DOCUMENT_TYPES.includes(data.documentType) && data.relatedDocumentReference?.reference) this.loadNoteOriginal(data);
    this.applyResolvedPaymentTerm(data);
    // #4785 — tax is API-owned (0 until the tax engine, #4268).
    this.documentTaxAmount.set(data.taxAmount ?? 0);

    this.form.patchValue(
      {
        documentType: data.documentType,
        noteReason: data.noteReason ?? null,
        noteCause: data.noteCause ?? null,
        documentDate: data.documentDate ? new Date(data.documentDate) : null,
        receivedDate: data.receivedDate ? new Date(data.receivedDate) : null,
        postingDate: data.postingDate ? new Date(data.postingDate) : null,
        fiscalNumber: data.fiscalNumber ?? null,
        providerReferenceNumber: data.providerReferenceNumber ?? null,
        cai: data.cai ?? null,
        dueDate: data.dueDate ? new Date(data.dueDate) : null,
        currency: data.currency,
        functionalAmount: data.functionalAmount ?? null,
        notes: data.notes ?? null,
      },
      { emitEvent: false },
    );

    // Purchase Receipt drafts keep the expected-number banner live; posted purchase receipts show the assigned number.
    if (data.documentType === ApDocumentType.PurchaseReceipt && data.status === ApDocumentStatus.Draft) this.loadPurchaseReceiptPreview();

    if (data.providerReference) {
      const pseudoProvider: Business = {
        id: data.providerReference,
        name: data.providerName ?? data.providerCode ?? '',
        code: data.providerCode,
        // Tax id is sourced from Business.TaxIndicatorCode.
        taxIndicatorCode: data.providerTaxId ?? undefined,
        businessTypes: [BusinessEntityType.Provider],
        providerDetails: {
          apGlAccountReference: data.apAccountReference ?? undefined,
          apGlAccountNumber: data.apAccountNumber ?? undefined,
          // #4785 — snapshotted on the document, so a posted doc keeps the name it had.
          apGlAccountName: data.apAccountName ?? undefined,
          creditDays: data.creditDays,
        },
      };
      this.form.controls.provider.setValue(pseudoProvider, { emitEvent: false });
      this.selectedProvider.set(pseudoProvider);
      // #5229 — same as the note path: the document snapshots no country, so the CAI rule needs it fetched.
      this.hydrateProviderCountry(data.providerReference);
      // The AP control account this document is pinned to, plus the provider's current one so the readout
      // can flag that the two have drifted apart. The pinned pair is authoritative for display and stays put
      // no matter what happens to the provider master.
      this.documentApAccountReference.set(data.apAccountReference ?? null);
      this.documentApAccountNumber.set(data.apAccountNumber ?? null);
      this.documentApAccountName.set(data.apAccountName ?? null);
      this.providerCurrentApAccountReference.set(data.providerApAccountReference ?? null);
      this.providerCurrentApAccountNumber.set(data.providerApAccountNumber ?? null);
      if (data.apAccountReference && !data.apAccountName) this.resolveDocumentApAccountName(data.apAccountReference);
      // The GL entry this document booked (#5031). Only a posted document has one.
      this.journalEntryReference.set(data.journalEntryReference ?? null);
      this.journalEntryDocumentNumber.set(data.journalEntryDocumentNumber ?? null);
      // Same entry id on a reload still means refetch it — see journalEntryReloadToken.
      this.journalEntryReloadToken.update((n) => n + 1);
      // Provider is locked after creation.
      this.form.controls.provider.disable({ emitEvent: false });
      // Load the provider's open POs (item-driven types only) + CAIs so more can be added to a draft.
      if (this.useItemTypeAhead()) this.loadProviderPurchaseOrders(data.providerReference);
      this.loadProviderCais(data.providerReference);
    }

    this.lines.clear();
    const fromReceipt = data.preInvoiceReference?.entityDefinitionCode === ORDER_RECEIPT_ENTITY_CODE;
    for (const item of data.lineItems ?? []) {
      const isPoLine = !!item.purchaseOrderKey?.reference;
      // #6537 — a charge added to an Order-Invoice-sourced pre-invoice: it is the clerk's, so neither lock applies.
      const isPreInvoiceCharge = !!data.preInvoiceReference && !fromReceipt && !isPoLine && !item.purchaseOrderItemReference;
      const sourceOwned = !!data.preInvoiceReference && !isPreInvoiceCharge;
      const group = this.buildLineGroup();
      group.patchValue(
        {
          itemSelection: item.itemCode ? this.pseudoItem(item.itemCode, item.description ?? '') : null,
          description: item.description ?? null,
          glAccountReference: item.glAccount?.reference ?? null,
          glAccountNumber: item.glAccount?.number ?? null,
          glAccountName: null,
          uom: item.unitOfMeasure ?? null,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          purchaseOrderItemId: item.purchaseOrderItemReference ?? null,
          purchaseOrderReference: item.purchaseOrderKey?.reference ?? null,
          purchaseOrderNumber: item.purchaseOrderKey?.number ?? null,
          sourceLineNumber: item.sourceLineNumber ?? null,
          // #4842 — the classification as SAVED. For an item line resolveLineItems re-locks it to the item
          // master once that lands; for a description-only line this is the clerk's own earlier choice and
          // stays editable.
          itemClassificationCode: item.itemClassificationCode ?? null,
          ...this.noteControlsFrom(item),
          // #7052 — a saved note line is one the clerk checked: only checked lines are ever sent.
          noteSelected: !_.isNil(item.noteAdjustment),
        },
        { emitEvent: false },
      );
      // #4842 — an item line's classification is the master's, not the clerk's, so lock the control. The
      // value patched above is the saved one; resolveLineItems replaces it with the master's shortly after.
      if (item.itemCode) group.controls.itemClassificationCode.disable({ emitEvent: false });
      // PO-sourced lines keep their price locked to the PO.
      // Pre-Invoice docs are a read-only preview: qty AND price belong to the source document, so both are
      // locked and only the GL account is editable. Reopening one has to re-apply BOTH locks, exactly as
      // buildPreInvoiceLines / buildPreReceiptLines do on the create path. Price used to hang off the
      // PO-line test alone, which a receipt-sourced line fails — a receipt has no PO — so the price and the
      // total (it back-solves the price) silently became editable the moment the document was reopened.
      if (isPoLine || sourceOwned) group.controls.unitPrice.disable({ emitEvent: false });
      if (sourceOwned) group.controls.quantity.disable({ emitEvent: false });
      this.lines.push(group);
    }
    if (this.lines.length === 0 && !this.useItemTypeAhead()) this.addLine();
    // #4842 — the withholding requirement as the API derived it, plus the waiver if one was recorded.
    this.requiresWithholding.set(data.requiresWithholding ?? true);
    this.withholdingWaivedReason.set(data.withholdingWaivedReason ?? null);
    this.withholdingWaivedBy.set(data.withholdingWaivedBy ?? null);
    // #5166 — the forced override, and whether any certificate already cites the document (which is what
    // makes releasing it impossible). Both are read from the server rather than inferred here.
    this.withholdingForced.set(data.withholdingForced ?? false);
    this.withholdingForcedReason.set(data.withholdingForcedReason ?? null);
    this.withholdingForcedBy.set(data.withholdingForcedBy ?? null);
    // Asked whenever a requirement exists, since removing one — waiving it OR undoing a forced one — is what
    // an existing certificate blocks.
    if (data.requiresWithholding && data.id) this.loadCitingCertificateCount(data.id);
    else this.citingCertificateCount.set(0);

    // #5165 — the receiving dates this document's POs can offer, and the rules that depend on its kind.
    // After the lines are built, since the PO set is derived from them.
    this.loadReceivedDateOptions();
    this.applyReceivedDateRules();

    // The rate the document was BOOKED at leads on reopen — never re-quote a booked document at today's rate.
    this.bookedRateToUsd.set(data.rateToUsd ?? null);
    this.bookedRateSource.set(data.rateSource ?? null);
    // Distinguishes "rate from receipt" vs "rate from order invoice" in the label for a pre-invoice source.
    if (data.preInvoiceReference)
      this.sourceRateOrigin.set(data.preInvoiceReference.entityDefinitionCode === ORDER_RECEIPT_ENTITY_CODE ? 'receipt' : 'invoice');

    // A typed rate survives a re-save only if it is still IN the payload.
    //
    // A draft is re-resolved from scratch every time it is saved — UpdateDraftAsync → BuildDraftAsync →
    // ResolveExchangeRateAsync — and the form sends `rateToUsd: v.rateToUsd ?? null`. The control was never
    // loaded back from the document, so reopening a manually-rated draft and pressing Save sent a null,
    // the API skipped its manual branch, and the document silently re-booked at the day's rate. The clerk
    // saw the override on screen the whole time, because that readout came off a separate signal.
    if (data.rateSource === ApRateSource.Manual && data.rateToUsd !== null && data.rateToUsd !== undefined) {
      this.form.controls.rateToUsd.setValue(data.rateToUsd);
      this.rateOverrideOn.set(true);
    }

    // Always, not only when nothing was booked. Cancelling an override or pressing reload needs the table's
    // answer already in hand, and a draft re-resolves against it on the next save regardless.
    this.loadRateInForce(data.currency);
    this.selectedCurrency.set(data.currency);
    // Currency is fixed once the document exists — unchangeable after creation (a saved doc is always
    // "created"), independent of pre-invoice vs normal. Non-draft docs are fully disabled below anyway.
    this.form.controls.currency.disable({ emitEvent: false });
    this.resolveLineItems(data.lineItems ?? []);
    this.refreshAfterLineChange();

    // #4724 — hydrate the per-PO-item invoiced rollup for any PO lines already on the document.
    const loadedPoRefs = Array.from(
      new Set(this.lines.controls.map((l) => l.controls.purchaseOrderReference.value).filter((r): r is string => !!r)),
    );
    for (const ref of loadedPoRefs) this.loadPoInvoicedSummary(ref);

    if (this.status() !== ApDocumentStatus.Draft) this.form.disable({ emitEvent: false });
  }

  /**
   * #4727 — re-hydrate every item line's item master (by code) in place: refreshes the item + its
   * configured GL accounts (so the per-line GL picker picks up accounts added on the master after the
   * line was created) and the UOM. Quantities, prices and GL selections are preserved. No item lines →
   * a no-op with an info toast.
   */
  reloadAllItems(): void {
    const lineItems = this.lines.controls.map((l) => ({ itemCode: l.controls.itemSelection.value?.itemCode }) as ApDocumentLine);
    if (!lineItems.some((l) => !!l.itemCode)) {
      GlobalMessageUtil.info(
        this.t('ApDocument_Items_Reloaded_None_Title', 'No item lines'),
        this.t('ApDocument_Items_Reloaded_None', 'There are no item-based lines to reload.'),
      );
      return;
    }
    this.startLoading();
    this.resolveLineItems(
      lineItems,
      () => {
        this.stopLoading();
        GlobalMessageUtil.success(
          this.t('ApDocument_Items_Reloaded_Title', 'Items refreshed'),
          this.t('ApDocument_Items_Reloaded', 'The item masters were reloaded for all lines.'),
        );
      },
      true,
    );
  }

  /**
   * Refreshes the real item + unit of measure on loaded lines. Lines saved before UOM was tracked
   * have no persisted unitOfMeasure, so resolve it from the item master by code. Optionally invokes
   * onComplete once the refresh settles (used by the #4727 manual "reload all items" action).
   */
  private resolveLineItems(lineItems: ApDocumentLine[], onComplete?: () => void, refreshDerivedAccounts = false): void {
    const itemCodes = Array.from(new Set(lineItems.map((l) => l.itemCode).filter((code): code is string => !!code)));
    if (itemCodes.length === 0) {
      onComplete?.();
      return;
    }

    this.itemService.getByItemCodes(itemCodes).subscribe({
      next: (response) => {
        const byCode = new Map((response.data ?? []).map((item) => [item.itemCode, item]));
        this.lines.controls.forEach((line) => {
          const code = line.controls.itemSelection.value?.itemCode;
          const item = code ? byCode.get(code) : undefined;
          if (!item) return;
          line.controls.itemSelection.setValue(item, { emitEvent: false });
          if (item.unitOfMeasure != null) line.controls.uom.setValue(item.unitOfMeasure, { emitEvent: false });
          // #4842 — now that the real item master is in hand, lock the line to ITS classification. Until this
          // point the line held only a pseudo-item (code + name), so the control was disabled around a value
          // that came from the saved document rather than from the master.
          this.syncLineClassification(line, item);
        });
        // The master is only now in hand, so this is the first point a goods line can be matched to its
        // account. On the manual reload it also RE-derives: a locked line is the one place the clerk cannot
        // fix an account themselves, so reloading the master has to be able to move it.
        this.applyGlAccountRules(refreshDerivedAccounts);
        onComplete?.();
      },
      error: () => onComplete?.(),
    });
  }

  // ---- save / post ----
  private buildPayload(): ApDocumentInsert {
    const v = this.form.getRawValue();
    const provider = this.selectedProvider();
    // A note sends only the lines it adjusts (#6324): the ones the clerk checked (#7052). validateDraft has
    // already refused a checked line with nothing valid in it.
    const sentLines =
      this.isNote() && !this.isFinancialNote() ? this.lines.controls.filter((line) => this.isNoteLineSelected(line)) : this.lines.controls;
    const lineItems: ApDocumentLineInsert[] = sentLines.map((line, index) => {
      const lv = line.getRawValue();
      // #7051 — a note line shows its original's PO but is not an invoice of it, so it sends none.
      const poRef = this.isNote() ? null : lv.purchaseOrderReference;
      return {
        lineNumber: index + 1,
        // Any line that carries a selected item sends its code (Invoice PO lines + searched items on a
        // Receipt / Purchase Receipt). Free-text lines have no itemSelection → null.
        itemCode: lv.itemSelection?.itemCode ?? null,
        description: lv.description ?? null,
        unitOfMeasure: lv.uom ?? null,
        // Amount-only kinds (#4462): the amount travels in unitPrice with qty 1 (API forces it too).
        quantity: this.isAmountOnly() ? 1 : lv.quantity,
        unitPrice: lv.unitPrice,
        taxRate: lv.taxRate ?? 0,
        glAccount: lv.glAccountReference ? { reference: lv.glAccountReference, number: lv.glAccountNumber ?? null } : null,
        purchaseOrderKey: poRef ? { reference: poRef, number: lv.purchaseOrderNumber ?? null } : null,
        purchaseOrderItemReference: this.isNote() ? null : (lv.purchaseOrderItemId ?? null),
        // Debit/Credit Note (#4471): the original invoice line this line adjusts.
        sourceLineNumber: this.isFinancialNote() ? null : (lv.sourceLineNumber ?? null),
        // #6324 — what the note changes on this line; the API derives the amounts and account from it.
        noteAdjustment: this.isNote() ? this.noteAdjustmentOf(line) : null,
        // #4842 — the API marks this `required`, so it must be present on every line or the whole payload
        // fails JSON binding. getRawValue() is used above, so a disabled (item-sourced) control still
        // contributes its value. Empty string, never null: the API reads blank as "unknown" and resolves it
        // to service — the safe direction — whereas null would fail binding outright.
        itemClassificationCode: lv.itemClassificationCode ?? '',
      };
    });

    // Pre-Invoice (#4306): the provider + lines are now populated client-side on selection, so they are sent
    // normally; only the reference marks the source. The server trusts them (rebuilds only if absent) and
    // takes the exchange rate from the invoice. v.currency is the (locked) PO currency.
    const preInvoice = this.usePreInvoice() ? this.selectedPreInvoice() : null;

    // One-time vendor Purchase Receipt (#4466/#4467): no provider master — the vendor is a free-text name
    // and the CxP control account is user-selected. The fiscal number/CAI are API-owned (#4465).

    // Debit/Credit Note (#4471): the source invoice it adjusts (provider + currency validated API-side).
    // On a REOPENED note the picker is gone (the link is locked after creation) and selectedSourceInvoice is
    // empty, so fall back to the reference the document was loaded with — otherwise the update sends a null
    // relatedDocumentReference and the API rejects the save for referencing no invoice.
    const sourceInvoice = this.isNote() ? this.selectedSourceInvoice() : null;
    const sourceInvoiceReference = this.isNote() && !this.isFinancialNote() ? (sourceInvoice?.id ?? this.noteSourceReference()) : null;
    const sourceInvoiceNumber = sourceInvoice?.number ?? this.noteSourceNumber();

    return {
      documentType: v.documentType,
      noteReason: this.isNote() ? v.noteReason : null,
      noteCause: this.isNote() ? v.noteCause : null,
      providerReference: provider?.id ?? null,
      financialCompanyReference: this.companyReference,
      documentDate: this.toIso(v.documentDate),
      // #5165 — the API re-derives this from the shipment on a document registered from an Order Invoice,
      // so what is sent there is ignored rather than trusted.
      receivedDate: v.receivedDate ? this.toIso(v.receivedDate) : null,
      postingDate: this.toIso(v.postingDate),
      creditDays: provider?.providerDetails?.creditDays ?? 0,
      // #4785 — sent so the API can honour an override; it is discarded server-side whenever a source PO or
      // Receipt dictates the term, so this can never quietly beat the source document.
      paymentTermCode: this.form.controls.paymentTermCode.value ?? null,
      currency: v.currency,
      fiscalNumber: this.isSelfIssued() ? null : (v.fiscalNumber ?? null),
      providerReferenceNumber: v.providerReferenceNumber ?? null,
      cai: this.takesCai() ? (v.cai ?? null) : null,
      providerTaxId: provider?.taxIndicatorCode ?? null,
      // Functional amount is always API-derived from the daily rate now (#4785 removed manual entry).
      functionalAmount: null,
      // The rate the user typed, if they did (#5135). Null leaves the API's ordinary chain alone — source
      // document, then PO, then the day — which is what happens for almost every document.
      rateToUsd: v.rateToUsd ?? null,
      // The account is pinned at registration, so a saved document sends back its own rather than the
      // provider's current one. The API ignores both (it re-derives from the stored document / the provider),
      // but sending the provider's here would put an account in the payload this document never posts to.
      apAccountReference: this.documentApAccountReference() ?? provider?.providerDetails?.apGlAccountReference ?? null,
      apAccountNumber: this.documentApAccountNumber() ?? provider?.providerDetails?.apGlAccountNumber ?? null,
      // apAccountName is deliberately NOT sent — the API snapshots it from the provider (#4785).
      notes: v.notes ?? null,
      preInvoiceReference: preInvoice
        ? {
            reference: preInvoice.id,
            // #4767 — the entity code is what tells the API which Order collection to resolve + consume.
            entityDefinitionCode: preInvoice.receipt ? ORDER_RECEIPT_ENTITY_CODE : ORDER_INVOICE_ENTITY_CODE,
            number: preInvoice.number || null,
          }
        : null,
      relatedDocumentReference: sourceInvoiceReference
        ? { reference: sourceInvoiceReference, number: sourceInvoiceNumber ?? null, entityDefinitionCode: AP_DOCUMENT_ENTITY_CODE }
        : null,
      lineItems,
    };
  }

  /** A saved note line's adjustment, as the line's form values (#6324). Empty for any other document kind. */
  private noteControlsFrom(item: ApDocumentLine): Partial<ReturnType<FormGroup<ApDocumentLineFormGroup>['getRawValue']>> {
    const adjustment = item.noteAdjustment;
    if (!adjustment) return {};
    return {
      noteQuantity: adjustment.quantity ?? null,
      notePriceMode: adjustment.priceMode ?? null,
      noteCorrectUnitPrice: adjustment.correctUnitPrice ?? null,
      noteAmount: adjustment.amount ?? null,
      noteAdjustsTax: adjustment.adjustsTax ?? false,
      noteCorrectTaxRate: adjustment.correctTaxRate ?? null,
      sourceQuantity: adjustment.sourceQuantity ?? 0,
      sourceUnitPrice: adjustment.sourceUnitPrice ?? 0,
      sourceLineTotal: adjustment.sourceLineTotal ?? 0,
      sourceTaxRate: adjustment.sourceTaxRate ?? 0,
      sourceTaxAmount: adjustment.sourceTaxAmount ?? 0,
    };
  }

  /**
   * A saved note's original (#6324): its balance feeds the summary and its certificates the warning. On a
   * DRAFT the original's other lines are added back, empty, so the clerk can still adjust a line the first
   * save left out — the saved document only carries the lines that were filled in.
   */
  private loadNoteOriginal(data: ApDocument): void {
    const originalId = data.relatedDocumentReference?.reference;
    if (_.isNil(originalId)) return;
    this.loadNoteCertificateWarning(originalId);
    this.service.getById(originalId).subscribe({
      next: (response) => {
        const original = response.data ?? null;
        this.noteOriginal.set(original);
        if (original) this.applyNoteLinePurchaseOrders(original);
        if (_.isNil(original) || data.status !== ApDocumentStatus.Draft) {
          this.recomputeTotals();
          this.recomputeGroups();
          this.loadNoteLineAccountNames();
          return;
        }
        const saved = new Set(this.lines.controls.map((line) => line.controls.sourceLineNumber.value));
        for (const sourceLine of original.lineItems ?? [])
          if (!saved.has(sourceLine.lineNumber)) this.lines.push(this.buildNoteLine(sourceLine));
        const ordered = [...this.lines.controls].sort(
          (a, b) => (a.controls.sourceLineNumber.value ?? 0) - (b.controls.sourceLineNumber.value ?? 0),
        );
        this.lines.clear({ emitEvent: false });
        for (const line of ordered) this.lines.push(line, { emitEvent: false });
        this.refreshAfterLineChange();
        this.loadNoteLineAccountNames();
      },
      error: () => this.noteOriginal.set(null),
    });
  }

  private toIso(value: Date | null): string {
    return value ? value.toISOString() : new Date().toISOString();
  }

  /** The gates a draft must pass before its form is sent anywhere — shared by Save and by Post, which
   *  saves first (what you see is what posts). */
  private validateDraft(): boolean {
    // Debit/Credit Note (#6324): a reason, and something to adjust.
    if (this.isNote() && this.noteReason() === null) {
      GlobalMessageUtil.warn(
        this.t('ApDocument_Note_ReasonRequired_Title', 'Reason required'),
        this.t('ApDocument_Note_ReasonRequired', 'Choose the reason for the note: Quantity, Price or value, Tax or Financial.'),
      );
      return false;
    }
    if (this.isNote() && !this.isFinancialNote() && !this.noteHasContent()) {
      GlobalMessageUtil.warn(
        this.t('ApDocument_Note_NothingEntered_Title', 'Nothing to adjust'),
        this.t('ApDocument_Note_NoLineChecked', 'Check at least one line of the original that the note adjusts.'),
      );
      return false;
    }
    // #7052 — a checked line is part of the note, so it must say what changes on it; silently dropping it
    // would save a different note from the one on screen.
    const emptyCheckedLines = this.isNote() && !this.isFinancialNote() ? this.checkedNoteLinesWithoutAdjustment() : [];
    if (emptyCheckedLines.length > 0) {
      GlobalMessageUtil.warn(
        this.t('ApDocument_Note_NothingEntered_Title', 'Nothing to adjust'),
        `${this.t('ApDocument_Note_CheckedLineEmpty', 'Enter what the note changes on each checked line, or uncheck it. Lines:')} ${emptyCheckedLines.join(', ')}`,
      );
      return false;
    }
    // Debit/Credit Note: require the source invoice (provider + lines are seeded from it). Financiera has none.
    if (this.isNote() && !this.isFinancialNote() && !(this.selectedSourceInvoice()?.id ?? this.noteSourceReference())) {
      GlobalMessageUtil.warn(
        this.t('ApDocument_Note_SourceRequired_Title', 'Source invoice required'),
        this.t('ApDocument_Note_SourceRequired', 'Select the invoice this note adjusts.'),
      );
      return false;
    }
    // Pre-Invoice mode: require a selected invoice (provider + lines are server-resolved on create).
    if (this.usePreInvoice() && this.isNew()) {
      if (!this.selectedPreInvoice()?.id) {
        GlobalMessageUtil.warn(
          this.t('ApDocument_PreInvoice_Required_Title', 'Source required'),
          this.t('ApDocument_PreInvoice_Required', 'Select a pre-invoice or receipt to register from.'),
        );
        return false;
      }
    } else if (this.lines.length === 0) {
      // At least one line is always required — PO-sourced for Invoices, manual for free-description
      // types (Receipt/…, #4462).
      this.form.controls.provider.markAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    // #4842 — an item whose master has no classification. Blocked rather than saved blank: the control is
    // disabled, so Validators.required never runs on it, and Angular leaves a disabled control out of
    // form.invalid entirely — the check below cannot catch this. Saving anyway would post a line that reads
    // as a service and demands a Constancia nobody asked for. Name the items, and name who can fix it.
    //
    // Runs BEFORE the form.invalid check on purpose: such a line also has its GL account picker locked
    // (lineGlAccountLocked), so a new line's required glAccountReference is blank and unopenable. Checked
    // the other way round, the clerk would be told "GL account required" about a field they cannot fill,
    // and never see the message that names what actually has to happen.
    const unclassifiedItems = this.unclassifiedItemCodes();
    if (unclassifiedItems.length > 0) {
      GlobalMessageUtil.warn(
        this.t('ApDocument_Line_Classification_MissingTitle', 'Item classification required'),
        `${this.t(
          'ApDocument_Line_Classification_MissingDetail',
          'These items have no classification in the item master. Ask Data Master to assign one before saving:',
        )} ${unclassifiedItems.join(', ')}`,
      );
      return false;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    // #4724 / #6538 — no client-side over-invoice block: the API owns the PO item cap and its switch, and a
    // refused save comes back as its 409 (the line already shows the red indicator).
    if (!this.companyReference) {
      console.error('No site selected; cannot save AP document.');
      return false;
    }
    return true;
  }

  saveDraft(): void {
    if (!this.validateDraft()) return;
    const payload = this.buildPayload();
    const wasPreInvoiceCreate = this.usePreInvoice() && this.isNew();
    this.startLoading();
    const existingId = this.currentId();
    const request = !this.isNew() && existingId ? this.service.updateDraft(existingId, payload) : this.service.createDraft(payload);
    request.pipe(finalize(() => this.stopLoading())).subscribe({
      next: (response) => {
        const created = response.data;
        if (created?.id) {
          this.isNew.set(false);
          this.currentId.set(created.id);
          this.status.set(created.status);
          this.documentNumber.set(created.number ?? '');
          // Header now reflects the assigned document number (was stuck on "New" until a reload).
          this.headerTitle.set(`${this.t('ApDocument', 'AP Document')} ${created.number ?? ''}`.trim());
          // Currency is locked once the document exists (unchangeable after creation).
          this.form.controls.currency.disable({ emitEvent: false });
          // #4784 — adopt the term the server resolved (source PO / Receipt beats the provider default),
          // then reflect the API-computed due date it produced.
          this.applyResolvedPaymentTerm(created);
          this.documentTaxAmount.set(created.taxAmount ?? 0);
          // #4842 — adopt the requirement the server just re-derived from the line classifications. The
          // save above flips isPersisted() true, which renders the withholding panel; without this it
          // reads the signal's `true` initializer on a create (or the pre-edit value on an update), so a
          // goods-only document claims it needs a Constancia until the page is reloaded.
          this.requiresWithholding.set(created.requiresWithholding ?? true);
          this.withholdingWaivedReason.set(created.withholdingWaivedReason ?? null);
          this.withholdingWaivedBy.set(created.withholdingWaivedBy ?? null);
          if (created.documentDate) this.form.controls.documentDate.setValue(new Date(created.documentDate), { emitEvent: false });
          if (created.dueDate) this.form.controls.dueDate.setValue(new Date(created.dueDate), { emitEvent: false });
          this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
          // Pre-Invoice (#4306): the server resolved the provider + lines — reload to show them (locked).
          if (wasPreInvoiceCreate) {
            this.usePreInvoice.set(false);
            this.selectedPreInvoice.set(null);
            this.loadDocument(created.id);
          }
        }
      },
      error: (error) => console.error('Error saving AP document draft:', error),
    });
  }

  post(): void {
    const id = this.currentId();
    if (!id || this.isNew()) return;
    // Same gates as Save, and before the confirmation dialog — a form that cannot be saved cannot be
    // posted, and it is better to hear that with the fields highlighted than after saying "yes".
    if (!this.validateDraft()) return;
    this.confirmationService.confirm({
      header: this.t('ApDocument_Post_Confirm_Header', 'Post Document'),
      message: this.t(
        'ApDocument_Post_Confirm_Message',
        'Posting saves your changes, creates the journal entry and locks this document. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.doPost(id),
    });
  }

  private doPost(id: string): void {
    this.startLoading();
    // Save first, post second — what you see is what posts. The screen can hold edits the stored draft
    // does not (a corrected exchange rate, an added line), and posting reads the STORED document; posting
    // directly silently booked the pre-edit draft, and a draft whose stored rate was never resolved died
    // at post with "no exchange rate is available" while the screen sat there displaying one.
    //
    // If the save fails, the post never runs. If the save lands and the post then fails, the edits are at
    // least saved — reopening the draft shows exactly what the refusal was about.
    this.service
      .updateDraft(id, this.buildPayload())
      .pipe(
        switchMap(() => this.service.post(id)),
        finalize(() => this.stopLoading()),
      )
      .subscribe({
        next: () => this.router.navigate(['/ap-documents']),
        error: (error) => console.error('Error posting AP document:', error),
      });
  }

  back(): void {
    this.router.navigate(['/ap-documents']);
  }

  /** Opens the cancel dialog on a posted document, with a fresh reason field. */
  openCancelDialog(): void {
    if (!this.canOfferCancel()) return;
    this.cancelReason.reset('');
    this.cancelDialogVisible.set(true);
  }

  /**
   * Cancels this posted document.
   *
   * <p>The whole page is reloaded rather than patched from the response: the document comes back Cancelled,
   * which changes what every section of this screen may do — no posting, no payment, no certificate — and
   * reloading is how the other terminal transitions here settle that rather than each panel being told.</p>
   *
   * <p>Errors are left to the global handler on purpose. The API refuses with the record standing in the way
   * named in the message ("cancel Constancia RET-2026-000012 first"), and that sentence is more use to the
   * clerk than anything this component could compose from a status code.</p>
   */
  confirmCancel(): void {
    const id = this.currentId();
    if (!id || !this.canOfferCancel()) return;

    const reason = this.cancelReason.value.trim();
    this.cancelSaving.set(true);
    this.service
      .cancel(id, reason || undefined)
      .pipe(finalize(() => this.cancelSaving.set(false)))
      .subscribe({
        next: () => {
          this.cancelDialogVisible.set(false);
          GlobalMessageUtil.success(
            this.t('ApDocument_Cancel_Done_Title', 'Document cancelled'),
            this.t('ApDocument_Cancel_Done', 'The journal entry was reversed and the document is cancelled.'),
          );
          this.loadDocument(id);
        },
        error: () => undefined,
      });
  }

  /**
   * Deletes this draft, after confirming (#5223).
   *
   * <p>Offered only on a SAVED draft: there is nothing to delete before the first save. The API applies the
   * same draft-only rule, so a record that moved on in another tab is answered by that refusal rather than
   * by whatever this screen last painted — the button is a convenience, not the guard.</p>
   *
   * <p>Hard delete: a draft has posted nothing, so there is no ledger entry to keep and nothing a surviving
   * row would explain. Hence the explicit warning that it cannot be undone.</p>
   */
  confirmDelete(): void {
    const id = this.currentId();
    if (!id || !this.isDraft()) return;
    this.confirmationService.confirm({
      header: this.t('ApDocument_Delete_Title', 'Delete draft'),
      message: this.t('ApDocument_Delete_Confirm', 'Delete this draft AP document? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Delete', 'Delete'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteDraft(id),
    });
  }

  private deleteDraft(id: string): void {
    this.service.delete(id).subscribe({
      next: () => {
        GlobalMessageUtil.success(
          this.t('ApDocument_Delete_Done_Title', 'Draft deleted'),
          this.t('ApDocument_Delete_Done', 'The draft was deleted.'),
        );
        this.router.navigate(['/ap-documents']);
      },
    });
  }
}
